import type { Api } from '../client'
import type {
  BulkRow,
  MarketSurvey,
  ChangeRequest,
  ChangeValue,
  DocSection,
  JobDescription,
  RoleSave,
  Level,
  Me,
  ProposableField,
  Role,
  UploadJob,
  VersionEntry,
  JdStatus,
  SurveyColumn,
  Benchmark,
  BenchmarkCut,
  BenchmarkStatus,
  RunProgress,
  MatchChat,
  MatchProgress,
  RangeRow,
  RangeState,
  PublishedRange,
  PublishedSet,
  ReadAs,
  FeedbackRecord,
  PendingFeedback,
  IconicAction,
  IconicChat,
  IconicTurn,
} from '../../types'
import { ApiError, LEVELS, STATUSES } from '../../types'
import { FAMILIES, industryFor, seedJds, seedRequests, seedVersions } from './seed'
import { extractDraft, readFromSections, rolesIn, sectionsFromText } from './extract'
import { builtSections, rowSections, rowText, sameSections, sectionsOf } from '../../lib/sections'
import { jdToDocument, jdsToCsv, splitCsvLine, holdsBulkRows, BULK_COLUMNS } from '../../lib/csv'
import { xlsxToCsv } from '../../lib/xlsx'
import { plural, splitCommas, splitLines, today } from '../../lib/format'
import { reasonHint } from '../../lib/feedback-view'
import { isStale, staleMessage } from '../../lib/changeRequests'
import {
  bandFor, accepted, waitingOn, spreadFor, spreadIsSane, DEFAULT_SPREAD, MOST_SPREAD,
  figureAt, offeredPcts, pctFor, pctLabel, isPct,
} from '../../lib/bands'
import type { Pct } from '../../lib/bands'
import {
  duplicatesAmong, freshMarket, listMarketData as queryMarket, readSurveyFile,
  removeSurvey as dropSurvey, storeSurvey, surveyFilters, columnValues,
  shownColumns,
} from './market'
import type { MarketDb } from './market'
import * as Admin from './admin'
import type { AdminDb, MockLogin } from './admin'

/**
 * A browser-side stand-in for the API in STE-536 Part 4, so the frontend can be
 * built and demonstrated before STE-540 lands. It answers the same shapes, keeps
 * the same rules (cross-tenant is 404, managers cannot publish, approving a
 * stale request is blocked) and persists to localStorage.
 *
 * Delete this directory once the real API is deployed; nothing outside it knows
 * the mock exists.
 */

const KEY = 'stello.jd.mock.v1'
const LATENCY = 260

/** Rows one mock commit imports, so a big file takes several like the real one. */
const MOCK_IMPORT_SLICE = 800

/** What a company decided about one role's range. The spread is absent while it is on the company's. */
interface RangeDecision {
  jdId: string
  median: number
  /** Which percentile that figure was read at. Absent reads as the median. */
  percentile?: Pct
  acceptedBy?: string
  acceptedAt?: string
  spread?: number
}

interface Db {
  jds: JobDescription[]
  versions: Record<string, VersionEntry[]>
  requests: ChangeRequest[]
  uploads: Record<string, UploadJob & { text?: string; startedAt?: number }>
  market: MarketDb
  admin: AdminDb
  /** One saved benchmark per job description, keyed by its id. */
  benchmarks?: Record<string, Benchmark>
  /** HR feedback on a benchmark, per role: the re-match waiting, and the record. */
  feedback?: Record<string, { pending: PendingFeedback | null; history: FeedbackRecord[] }>
  /** What this company decided about each role's range. Written only by a person. */
  ranges?: Record<string, RangeDecision>
  /** The company's spread, used by every role that has not been given its own. */
  companySpread?: number
  /** The set as it was published. Nothing afterwards changes it. */
  published?: PublishedSet
  runs?: Record<string, RunProgress>
  /** What each run still has to work through, so progress can be watched. */
  pendingRuns?: Record<string, string[]>
  /** Match a job: one conversation each, and the matches inside them. */
  chats?: Record<string, MatchChat>
  matches?: Record<string, MatchProgress>
  /** Iconic: each person's own chats, and their turns. */
  iconic?: { chats: Record<string, MockIconicChat>; turns: Record<string, MockIconicTurn[]> }
  /** What each match was asked, so the answer can be filed against it. */
  pendingAsks?: Record<string, string>
}

/**
 * The same people, with the same passwords, as the deployed system.
 *
 * This password is local only and opens nothing. It is deliberately not the
 * deployed demo password: sharing one string between a mock and a live account
 * is how a real credential ends up written into the repository.
 *
 * Exported so that tests and scripts use this constant rather than copying the
 * literal, which is the same mistake one step removed.
 */
export const DEMO_PASSWORD = 'local-demo' // not-a-credential: local mock only, opens nothing
const TENANT = { slug: 'stello.in', name: 'Stello' }
const SETTINGS = {
  jobFamilies: FAMILIES,
  levels: LEVELS as unknown as Level[],
  currency: 'USD',
}

const USERS: Record<string, { password: string; me: Omit<Me, 'pendingApprovals' | 'jdCount'> }> = {
  'hr@stello.in': {
    password: DEMO_PASSWORD,
    me: {
      userId: 'u-admin',
      name: 'HR Manager',
      email: 'hr@stello.in',
      role: 'admin',
      tenant: TENANT,
      settings: SETTINGS,
    },
  },
  'manager@stello.in': {
    password: DEMO_PASSWORD,
    me: {
      userId: 'u-manager',
      name: 'People Manager',
      email: 'manager@stello.in',
      role: 'manager',
      tenant: TENANT,
      settings: SETTINGS,
    },
  },
  // Sets up companies and the people in them, and can open nothing else. The
  // company shown beside the name is ours, not a customer's.
  'ops@stello.in': {
    password: DEMO_PASSWORD,
    me: {
      userId: 'u-super',
      name: 'Stello Operations',
      email: 'ops@stello.in',
      role: 'superadmin',
      tenant: { slug: 'stello-ops', name: 'Stello Operations' },
      settings: SETTINGS,
    },
  },
}

const SESSION_KEY = 'stello.jd.mock.session'

const wait = (ms = LATENCY) => new Promise((r) => setTimeout(r, ms))

/**
 * How far a survey has got, played out over half a minute (FR-M58, FR-M59).
 *
 * The stand-in stores a file at once, so a survey committed in this session
 * is shown filling for fifteen seconds, chunk by chunk, and then being
 * prepared for fifteen, the way the real worker reports it. Anything else
 * reads as ready, which is what every survey held on a real deployment
 * becomes.
 */
const committedAt = new Map<string, number>()
const STORE_MS = 15_000
const PREPARE_MS = 15_000
function withStatus(s: MarketSurvey): MarketSurvey {
  const at = new Date().toISOString()
  const since = committedAt.has(s.id) ? Date.now() - committedAt.get(s.id)! : Infinity
  if (since < STORE_MS) {
    const chunks = Math.max(1, Math.ceil(s.rowCount / 2000))
    const done = Math.floor((since / STORE_MS) * chunks)
    return { ...s, rowCount: 0, storing: true, progress: { stored: Math.min(s.rowCount, done * 2000), of: s.rowCount } }
  }
  const of = 2 * s.rowCount
  if (since < STORE_MS + PREPARE_MS) {
    return { ...s, preparation: { state: 'preparing', rows: Math.floor(((since - STORE_MS) / PREPARE_MS) * of), of, at } }
  }
  return { ...s, preparation: { state: s.rowCount ? 'ready' : 'empty', rows: of, of, at } }
}

/** Waits, unless the request is cancelled first, which rejects the way fetch does. */
const waitOr = (signal: AbortSignal | undefined, ms: number) =>
  new Promise<void>((resolve, reject) => {
    const gone = () => reject(new DOMException('The request was cancelled.', 'AbortError'))
    if (signal?.aborted) return gone()
    const timer = setTimeout(() => { signal?.removeEventListener('abort', stop); resolve() }, ms)
    const stop = () => { clearTimeout(timer); gone() }
    signal?.addEventListener('abort', stop, { once: true })
  })

/**
 * A slow, busy market search, for trying the screen the way uat behaves.
 *
 * Off unless asked for, by setting `stello.mock.slowMarket` in local storage
 * to something like `{"ms":900,"reads":400}`: each search then takes about
 * that long and reads only that many rows, so pages come back short with more
 * to read. A search of fewer than four letters takes three times as long,
 * because on the real table a short word is the one that keeps reading, and
 * that is what lets an early keystroke finish after a later one.
 */
function slowMarket(): { ms: number; reads: number } | null {
  try {
    const raw = localStorage.getItem('stello.mock.slowMarket')
    if (!raw) return null
    const knob = JSON.parse(raw) as { ms?: number; reads?: number }
    return { ms: Number(knob.ms) || 900, reads: Number(knob.reads) || 400 }
  } catch {
    return null
  }
}

const slowFor = (ms: number, q?: string) =>
  ms * ((q ?? '').trim().length < 4 ? 3 : 1) * (0.75 + Math.random() / 2)

/**
 * The run going right now, if any.
 *
 * One definition, used by the overview and by both ways of starting a run.
 * The stand-in disagreeing with itself about whether a run is going would let
 * a screen pass here and be wrong against the real thing.
 *
 * `stopping` counts as going, exactly as the server counts it: the roles
 * already started are still finishing.
 */
/**
 * How long one role takes, the same number the server uses.
 *
 * Most of it is waiting on purpose: a role costs about twelve Bedrock
 * requests and this account is allowed ten a minute, so roles are spaced out
 * rather than throttled. A stand-in that still said twenty would show a
 * cheerful estimate for a run that takes three times as long, and a stand-in
 * kinder than the product is the one thing it must never be.
 */
const SECONDS_EACH = 90

const liveRun = () =>
  Object.values(db.runs ?? {}).find((r) => r.state === 'running' || r.state === 'stopping')

function load(): Db {
  try {
    const raw = localStorage.getItem(KEY)
    // A library saved before companies existed has no admin section. Filling
    // the gap beats throwing the person's whole demo library away.
    if (raw) {
      const saved = JSON.parse(raw) as Db
      if (!saved.admin) saved.admin = Admin.freshAdmin(DEMO_PASSWORD)
      return saved
    }
  } catch {
    /* fall through to a fresh seed */
  }
  return fresh()
}

function fresh(): Db {
  return {
    jds: seedJds(),
    versions: seedVersions(),
    requests: seedRequests(),
    uploads: {},
    market: freshMarket(),
    admin: Admin.freshAdmin(DEMO_PASSWORD),
  }
}

let db: Db = load()

function save() {
  try {
    localStorage.setItem(KEY, JSON.stringify(db))
  } catch {
    /* a full quota should not break the session */
  }
}

/** Wipes the demo library so the empty-company state can be seen. */
export function resetMock(empty = false, opts: { withSurvey?: boolean } = {}) {
  db = empty
    ? { jds: [], versions: {}, requests: [], uploads: {}, market: freshMarket(), admin: Admin.freshAdmin(DEMO_PASSWORD) }
    : fresh()
  /*
   * One survey with no rows, for a suite that needs roles benchmarked without
   * walking an upload first. The mock's benchmark only asks whether a survey
   * exists; the real one reads its rows.
   */
  if (opts.withSurvey) {
    db.market.surveys.unshift({
      id: 'sv_test', provider: 'Radford', year: '2025', industry: 'Technology',
      uploadedBy: 'HR Manager', uploadedAt: '2026-10-01T00:00:00.000Z',
      rowCount: 1, files: [], columns: [], filters: [],
    } as (typeof db.market.surveys)[number])
  }
  save()
}

function session(): Me {
  const email = sessionStorage.getItem(SESSION_KEY) ?? localStorage.getItem(SESSION_KEY)
  if (!email) throw new ApiError(401, 'Your session has ended. Sign in again.')
  // The store first, for the same reason sign in reads it first.
  const made = madeUser(email)
  if (made) return withCounts(made)
  const built = USERS[email]
  if (!built) throw new ApiError(401, 'Your session has ended. Sign in again.')
  return withCounts(built.me)
}

/** Finds a person created through the companies screens, by their login. */
function madeUser(email: string): Omit<Me, 'pendingApprovals' | 'jdCount'> | null {
  for (const c of db.admin.companies) {
    const p = c.people.find((x) => x.email === email && !x.disabled)
    if (p) {
      return {
        userId: p.userId,
        name: p.name,
        email: p.email,
        role: p.role,
        tenant: { slug: c.slug, name: c.name },
        settings: SETTINGS,
      }
    }
  }
  return null
}

/** Looks a login up wherever it lives, built in or created during the demo. */
function findLogin(email: string): MockLogin | null {
  for (const c of db.admin.companies) {
    const p = c.people.find((x) => x.email === email)
    if (p) return p
  }
  return null
}

function requireSuperAdmin(me: Me) {
  // Says nothing about what the route does. Somebody who should not know this
  // part of the product exists should not learn it from a refusal.
  if (me.role !== 'superadmin') throw new ApiError(403, 'Not allowed.')
}

/** Who the history records as having done it. */
const actor = (me: Me) => ({ name: me.name, email: me.email })

function withCounts(me: Omit<Me, 'pendingApprovals' | 'jdCount'>): Me {
  const pending = db.requests.filter(
    (r) => r.status === 'pending' && (me.role === 'admin' || r.requestedBy === me.name),
  ).length
  return { ...me, pendingApprovals: pending, jdCount: db.jds.length }
}

function requireAdmin(me: Me) {
  // The UI never renders admin controls for a manager, and the API refuses them
  // anyway. Both halves of FR-A3 are enforced.
  if (me.role !== 'admin') throw new ApiError(403, 'Only HR admins can do that.')
}

const roles = () => db.jds

/** FR-2: all words must match across eight fields; qualifications are excluded. */
function matches(j: JobDescription, words: string[]) {
  if (!words.length) return true
  const hay = [j.title, j.family, j.level, j.location, j.status, j.summary]
    .concat(j.skills, j.responsibilities)
    .join(' ')
    .toLowerCase()
  return words.every((w) => hay.includes(w))
}

const words = (q: string) => q.trim().toLowerCase().split(/\s+/).filter(Boolean)

function draftToFields(d: RoleSave) {
  return {
    title: d.title.trim(),
    level: d.level as Level,
    location: d.location.trim(),
    payMin: parseFloat(d.payMin) || 0,
    payMax: parseFloat(d.payMax) || 0,
    // Only written when it is hourly. A role with no unit reads as a year,
    // the same way the route stores it.
    ...(d.payUnit === 'hour' ? { payUnit: 'hour' as const } : {}),
    ...hiddenOf(d),
  }
}

/** The hidden fields out of text, as the server's `hiddenFrom` stores them (FR-49). */
function hiddenOf(from: object) {
  const d = from as Partial<Record<string, string | undefined>>
  const kept = (name: string) => {
    const text = (d[name] ?? '').trim()
    return text === '' ? {} : { [name]: text }
  }
  return {
    summary: (d.summary ?? '').trim(),
    responsibilities: splitLines(d.responsibilities ?? ''),
    skills: splitCommas(d.skills ?? ''),
    quals: splitLines(d.quals ?? ''),
    ...kept('physicalDemands'), ...kept('workEnvironment'), ...kept('totalRewards'),
    ...kept('compensationNote'), ...kept('legal'),
  }
}

/** The sections a save sent, tidied as the server cleans them, or nothing. */
function sectionsSent(value: DocSection[] | undefined): DocSection[] | undefined {
  const out = (value ?? [])
    .map((s) => ({
      heading: s.heading.replace(/\s+/g, ' ').trim(),
      lines: s.lines.map((l) => l.replace(/\s+/g, ' ').trim()).filter(Boolean),
    }))
    .filter((s) => s.heading || s.lines.length)
  return out.length ? out : undefined
}

/** How long the stand-in takes to read the hidden fields again. Long enough to see. */
export const REFRESH_MS = 1500

/**
 * Reads a role's hidden fields again from its sections after a save changed
 * them (FR-50), a moment later, as the real queue does.
 *
 * Writes only the hidden fields, adds no version, and touches no key detail.
 * A role saved again with different sections in the meantime is left for
 * that save's own read (FR-50b).
 */
function queueRefresh(jdId: string, sections: DocSection[]) {
  setTimeout(() => {
    const now = db.jds.find((j) => j.id === jdId)
    if (!now?.sections?.length || !sameSections(now.sections, sections)) return
    const { physicalDemands: _a, workEnvironment: _b, totalRewards: _c, compensationNote: _d, legal: _e, ...rest } = now
    const next: JobDescription = { ...rest, ...hiddenOf(readFromSections(now.sections)) }
    db.jds = db.jds.map((j) => (j.id === jdId ? next : j))
    save()
  }, REFRESH_MS)
}

// Must match the backend's rule exactly. Runs of whitespace collapse, so
// "Senior  Engineer" and "Senior Engineer" are the same title. A stand-in that
// is more permissive than the real thing lets a breaking change pass offline.
const slug = (t: string) => t.trim().toLowerCase().replace(/\s+/g, ' ')

/**
 * The families this company uses: the ones it has configured plus the ones its
 * roles already carry. A family is free text now, so the configured list is
 * what a company has rather than all it may have, and a spreadsheet cell is
 * measured against both.
 */
const usedFamilies = () => [...new Set([...FAMILIES, ...roles().map((j) => j.family)])]

function validateBulkRow(
  line: string,
  index: number,
  seen: Map<string, number>,
  families: string[],
): BulkRow {
  const f = splitCsvLine(line)
  const get = (k: (typeof BULK_COLUMNS)[number]) => (f[BULK_COLUMNS.indexOf(k)] ?? '').trim()
  const errors: string[] = []

  const title = get('title')
  const family = get('family')
  const level = get('level')
  const payMin = get('payMin')
  const payMax = get('payMax')
  const status = get('status') || 'Published'

  // The columns are positional, so a line that stops early is ambiguous about
  // which field its last value was. A blank cell is fine and takes a default; a
  // missing column is not the same thing as a blank one.
  if (f.length < BULK_COLUMNS.length) {
    errors.push(
      `this line has ${f.length} column${f.length === 1 ? '' : 's'} and the spreadsheet has ` +
      `${BULK_COLUMNS.length}. Add the missing commas, even where the cells are empty.`,
    )
  }
  if (!title) errors.push('title is required')
  // Checked against the families the company uses, meaning the ones it has
  // configured plus the ones its roles already carry, which is the rule the
  // real checker applies. Measuring against the configured list alone made the
  // stand-in stricter than the product: a family read out of a document by the
  // AI, or typed by a person, was reported as unknown here and accepted there.
  if (family && !families.includes(family)) errors.push(`unknown job family "${family}"`)
  if (level && !(LEVELS as readonly string[]).includes(level)) errors.push(`unknown level "${level}"`)
  if (payMin && isNaN(parseFloat(payMin))) errors.push(`pay min "${payMin}" is not a number`)
  if (payMax && isNaN(parseFloat(payMax))) errors.push(`pay max "${payMax}" is not a number`)
  if (!(STATUSES as readonly string[]).includes(status)) {
    errors.push(`status "${status}" must be Draft, In review or Published`)
  }
  // The real checker has always caught this and the stand-in never did, so a
  // range the wrong way round imported cleanly offline and was refused live.
  if (payMin && payMax && parseFloat(payMin) > parseFloat(payMax)) {
    errors.push(`pay min ${payMin} is higher than pay max ${payMax}`)
  }
  // A shared title is counted, not refused. A company really does have two
  // roles called the same thing.
  const key = slug(title)
  const already = title ? (seen.get(key) ?? 0) : 0
  if (title) seen.set(key, already + 1)
  const sameTitle = already > 0 ? already : undefined

  // A blank cell takes a default, and the row says which ones did.
  const defaulted: string[] = []
  if (!family) defaulted.push('family')
  if (!level) defaulted.push('level')
  if (!get('location')) defaulted.push('location')

  return {
    line: index + 1,
    title,
    family: family || 'Software Engineering',
    level: level || 'Mid',
    location: get('location') || 'US national',
    payMin: parseFloat(payMin) || 0,
    payMax: parseFloat(payMax) || 0,
    summary: get('summary'),
    responsibilities: get('responsibilities'),
    skills: get('skills'),
    quals: get('qualifications'),
    status,
    errors,
    sameTitle,
    from: 'csv',
    ...(defaulted.length ? { guessed: defaulted } : {}),
  }
}

const id = (prefix: string) => prefix + Math.random().toString(36).slice(2, 8)

/**
 * The same page marker the real API uses: a position, never a count.
 *
 * A stand-in that paged by offset while the deployed one pages by position is
 * a suite that passes against a screen the product cannot actually drive.
 */
interface Mark { updated: string; id: string }

const encodeCursor = (mark: Mark) => btoa(JSON.stringify(mark))

function decodeCursor(cursor: string | null | undefined): Mark | null {
  if (!cursor) return null
  try {
    const got = JSON.parse(atob(cursor)) as Partial<Mark>
    return typeof got?.updated === 'string' && typeof got?.id === 'string'
      ? { updated: got.updated, id: got.id }
      : null
  } catch {
    // A cursor we cannot read means start again rather than fail the request.
    return null
  }
}

/** Newest change first, with the id breaking a tie. Same rule as the API. */
const byNewest = (a: { updated: string; id: string }, b: { updated: string; id: string }): number =>
  a.updated === b.updated
    ? (a.id < b.id ? 1 : a.id > b.id ? -1 : 0)
    : (a.updated < b.updated ? 1 : -1)

const isAfter = (e: { updated: string; id: string }, mark: Mark): boolean =>
  e.updated === mark.updated ? e.id < mark.id : e.updated < mark.updated


/**
 * A benchmark for one role, made up but made up consistently.
 *
 * Derived from the role's own pay range so the compa-ratio lands somewhere
 * believable, and seeded from the id so the same role gives the same answer on
 * every reload. A mock that shuffles its numbers makes every screen look
 * broken in a different way each time it is opened.
 *
 * One role in eight comes back with no match, because **no match is a state
 * the screens have to handle and the only way to see it is to produce it.**
 */
/** The state of this company's market data, as one short string. */
function marketStamp(): string {
  if (db.market.surveys.length === 0) return 'none'
  return db.market.surveys
    .map((sv) => [sv.id, sv.rowCount ?? 0, sv.uploadedAt ?? ''].join(':'))
    .sort()
    .join('|')
}

function makeBenchmark(jd: JobDescription): Benchmark {
  const seed = [...jd.id].reduce((total, ch) => total + ch.charCodeAt(0), 0)
  const runAt = new Date().toISOString()
  const from = db.market.surveys[0]

  if (seed % 8 === 0) {
    return {
      jdId: jd.id, jdVersion: jd.version, surveyId: from?.id ?? '',
      cuts: [], band: null, unit: null, currency: null,
      compaRatio: null, position: 'not benchmarked',
      confidence: 'none', confidenceReason: 'No cut survived the exclusions, so there is no band.',
      excluded: [
        { code: 'ENG.P2', title: `${jd.family} P2`, reason: 'too few organisations to publish (3, the floor is 5)' },
        { code: 'ENG.H1', title: `${jd.family} hourly`, reason: 'reported hourly, not annual' },
      ],
      /*
       * A real refusal names what came closest, with why each is not this
       * job (STE-1063). One that named nothing would hide the very list the
       * page and its Suggest this job buttons are built around.
       */
      rejected: [
        { code: 'GMA.02.001.M50', reason: `Filed under Strategy and Planning, which covers internal long-term strategy, not the ${jd.family} work this role does.` },
        { code: 'PPM.01.001.M50', reason: 'Filed under Program Management Office, which covers one part of this role but not what defines it.' },
        { code: 'SMP.02.021.M50', reason: 'Filed under Business Development Strategy and Planning, which covers one responsibility of the role, not the whole job.' },
      ],
      managesPeople: null, managesPeopleReason: '',
      reasoning: '',
      noMatchReason: `Nothing in this survey was close enough. The nearest 40 were considered and none described the same job.`,
      country: 'United States',
      runBy: session()?.name ?? 'You', runAt, model: 'local-mock',
    }
  }

  /*
   * A job description stores pay in thousands; a survey stores whole currency.
   * The band is built in the survey's units, not the role's, so this mock
   * exercises the same conversion the real product has to do.
   *
   * Deriving the band from the pay range in the role's own units would make
   * the mock agree with itself and hide a compa-ratio that is a thousand times
   * out. It did exactly that until 2026-09-26.
   */
  const mid = ((Number(jd.payMin) + Number(jd.payMax)) / 2 || 140) * 1000
  // A spread either side of what they pay, so some roles read above market and
  // some below rather than every one landing at exactly 1.00.
  const drift = 1 + ((seed % 21) - 10) / 100
  /*
   * And a shift that follows the market data the company holds.
   *
   * Without it the median was a function of the role alone, so adding a survey
   * changed no figure anywhere and a range could never have one waiting on it.
   * The real median comes from survey rows and moves when they do; this is the
   * cheapest stand-in for that which still moves for the same reason.
   */
  const marketSeed = [...marketStamp()].reduce((total, ch) => total + ch.charCodeAt(0), 0)
  // Folded with the role's own seed, so a market move does not shift every
  // role by exactly the same percentage, which no real survey ever does.
  const shift = 1 + (((marketSeed + seed) % 17) - 4) / 100
  const p50 = Math.round(((mid / drift) * shift) / 100) * 100
  /** Roughly one role in seven, which is close to the real file's four in 35. */
  const thin = seed % 7 === 0
  /*
   * Every cut says who published it and for which year.
   *
   * The real worker puts both on every cut, and the mock left them off, so the
   * "where each figure comes from" table read "not recorded" with a blank year
   * for a survey the person had just uploaded as Mercer 2026. A mock that
   * carries fewer fields than the product is a mock that agrees with itself:
   * the screen harness runs against it and would pass a screen that had lost
   * the source column altogether.
   */
  const cut = (
    code: string, title: string, at: number, incumbents: number, orgs: number,
    score: number, reason: string, source = from,
    split: { content: string; levelling: string; sameLevel: boolean } = {
      content: 'Same discipline. The duties named in the role appear in this survey job.',
      levelling: 'The scope described lines up with the level the provider assigns.',
      sameLevel: true,
    },
    description = '',
  ) => ({
    code, title, incumbents, orgs, weight: 0, score, reason,
    provider: source?.provider, year: source?.year,
    location: 'United States',
    /*
     * The named reasons, built the way the server builds them so the screen
     * harness exercises the real shape. Five of the seven are worked out from
     * the row rather than written, so the stand-in works them out too: one
     * that handed over seven ready-made sentences would pass a screen that
     * had stopped deriving any of them.
     */
    /*
     * Worked out from the same facts the reasons are, not written out
     * separately. A stand-in whose one line summary says "same level" beside
     * a reason saying "one level above" teaches the screen harness that the
     * two are allowed to disagree.
     */
    relation: `Same job family (${jd.family}), `
      + `${split.sameLevel ? 'same level' : 'survey level one above'}, United States cut`,
    surveyDescription: description,
    why: {
      content: split.content,
      levelling: split.levelling,
      skills: (jd.skills ?? []).length
        ? `This role lists ${(jd.skills ?? []).slice(0, 6).join(', ')}. Where a survey `
          + 'publishes a separate cut for a skill like these we read that one. This survey '
          + 'publishes one cut for this job, so that is what the figures are.'
        : 'This role lists no specific skills, so there is nothing here to read a narrower '
          + 'cut against.',
      industry: `Same industry cut (${source?.industry ?? 'Technology'}), which is the like `
        + 'for like read. The survey does not publish who took part.',
      sample: `${incumbents.toLocaleString()} people across ${orgs.toLocaleString()} `
        + 'organisations. Reliable at the median. The ends of the range always rest on fewer '
        + 'people than the middle, so treat the top as directional when setting a band maximum.',
      currency: `Effective ${source?.year ?? '2026'}, the current cycle. Nothing has been aged, `
        + 'so these are the figures exactly as published.',
      practice: 'We match on the written scope of the role and the level the provider assigns, '
        + 'not on job titles, because the same title covers different work at different '
        + 'companies. Where more than one survey has a job that fits, the figures are blended '
        + 'and weighted by how many people stand behind each one, and that weighting is shown '
        + 'so it can be changed. That is why this is a ranked shortlist you confirm rather '
        + 'than a single answer.',
    },
    /*
     * Not every provider publishes all four.
     *
     * A thin cut gets its outer percentiles suppressed, because too few
     * organisations stand behind them to report. On the demo company's real
     * data 31 of 35 banded roles have a 25th and a 75th, 35 have a median,
     * and not one has a 90th: a stand-in that filled in all four for
     * everybody would hide the screen that has to cope with the gaps.
     *
     * Deterministic from the role's own seed, so the same roles are thin on
     * every run and a harness can rely on at least one being there.
     */
    p25: thin ? null : Math.round(at * 0.87),
    p50: at,
    p75: thin ? null : Math.round(at * 1.14),
    // Nothing anywhere publishes a 90th, which is what the real file does.
    p90: null,
  })
  /*
   * The second cut comes from the second provider when there is one.
   *
   * Weighting only appears when a band draws on more than one provider, and
   * with every mock cut carrying the same provider that branch was
   * unreachable offline. A mock that can only produce the case where a
   * control is hidden cannot show that it appears when it should.
   */
  const alsoFrom = db.market.surveys.find((s) => s.provider !== from?.provider) ?? from
  const cuts = [
    cut(`${jd.family.slice(0, 3).toUpperCase()}.P3`, `${jd.family} ${jd.level}`, p50, 240, 30, 0.93,
      'Same discipline and seniority, and the responsibilities line up.', from),
    /*
     * The second one carries a provider description and the first does not,
     * because both states are real and the screen has to be right for each.
     * Most Mercer exports publish none.
     */
    cut(`${jd.family.slice(0, 3).toUpperCase()}.P4`, `${jd.family} one level up`, Math.round(p50 * 1.15), 80, 18, 0.71,
      'One level above, but the scope described overlaps.', alsoFrom,
      {
        content: 'Same discipline, and the duties overlap, but this survey job owns a larger '
          + 'piece of work than the role describes.',
        levelling: 'One level above. The provider puts this job a rung higher than the role '
          + 'is written, so read it as the ceiling rather than the comparison.',
        sameLevel: false,
      },
      'Owns a major component or subsystem, sets technical direction inside the team and '
        + 'mentors less experienced engineers. Impact is a multi-quarter team roadmap.'),
  ]
  const total = cuts.reduce((sum, c) => sum + c.incumbents, 0)
  for (const c of cuts) c.weight = Math.round((c.incumbents / total) * 1000) / 1000

  /*
   * Absent stays absent, the way the real blend does it.
   *
   * A percentile no cut published is left null rather than filled in from
   * the one beside it. A mock that invents one hides the screen that prints
   * it, and a range centred on an invented figure traces back to nothing.
   */
  const at = (pick: (c: typeof cuts[number]) => number | null) => {
    let sum = 0
    let weight = 0
    for (const c of cuts) {
      const v = pick(c)
      if (v === null) continue
      sum += v * c.incumbents
      weight += c.incumbents
    }
    return weight === 0 ? null : Math.round(sum / weight)
  }
  const band = {
    p25: at((c) => c.p25), p50: at((c) => c.p50)!,
    p75: at((c) => c.p75), p90: at((c) => c.p90),
  }
  const ratio = Math.round((mid / band.p50) * 1000) / 1000

  return {
    jdId: jd.id, jdVersion: jd.version, surveyId: from?.id ?? '',
    cuts, band, unit: 'annual', currency: 'USD',
    compaRatio: ratio,
    position: ratio > 1.05 ? 'above market' : ratio < 0.95 ? 'below market' : 'at market',
    confidence: 'high', confidenceReason: '48 organisations and 320 people across 2 cuts.',
    excluded: [
      { code: 'X.THIN', title: `${jd.family} narrow cut`, reason: 'too few organisations to publish (3, the floor is 5)' },
      { code: 'X.HOUR', title: `${jd.family} hourly`, reason: 'reported hourly, not annual' },
      { code: 'X.EUR', title: `${jd.family} Europe`, reason: 'reported in EUR, not USD' },
    ],
    rejected: [{ code: 'MGR.M1', reason: 'A management cut, and this job description names no direct reports.' }],
    managesPeople: false,
    managesPeopleReason: 'The responsibilities name no direct reports.',
    reasoning: 'Matched on discipline, seniority and the responsibilities listed. The one level above was included because the scope overlaps, weighted lower for the smaller sample behind it.',
    /*
     * The same reasoning under the matcher's own steps. Kept beside the
     * paragraph rather than instead of it, the way the product does, so the
     * screen harness exercises both the named reading and the fallback.
     */
    how: {
      ladder: 'The responsibilities name no direct reports and no budget, so this was read '
        + 'as an individual contributor before anything was searched for.',
      role: `Function is ${jd.family}. Area is the company's own industry. The focus is the `
        + `responsibilities listed on the role. Level is ${jd.level}.`,
      searched: `Searched for "${jd.family} ${jd.level}", for the function on its own, and for `
        + 'the two skills named most often in the responsibilities. All three came back to the '
        + 'same corner of the catalogue.',
      covers: `The survey files jobs under ${jd.family}, which is the work this role does, so `
        + 'the catalogue does cover it. This is checked before any rung, because a different '
        + 'discipline is not a lower rung.',
      level: `${jd.level} in the role lines up with the provider's own level of the same name. `
        + 'The rung above was kept as an alternate because its scope overlaps, not because it '
        + 'is nearer.',
      gaps: 'Nothing material. The survey job covers a slightly broader remit than the role '
        + 'describes, which is why the match is not scored higher.',
    },
    noMatchReason: null,
    country: 'United States',
    runBy: session()?.name ?? 'You', runAt, model: 'local-mock',
  }
}

/**
 * The decision a role must already have before a spread can be set on it.
 *
 * A role gets its first decision when it is first benchmarked, so one with
 * none has never been benchmarked and has no range for a spread to widen.
 */
function firstDecision(jdId: string): RangeDecision {
  db.ranges = db.ranges ?? {}
  const held = db.ranges[jdId]
  if (held) return held
  const market = (db.benchmarks ?? {})[jdId]?.band?.p50 ?? null
  if (market === null) throw new ApiError(409, 'That role has no range to set a spread on yet.')
  const first: RangeDecision = { jdId, median: market, percentile: 'p50' }
  db.ranges[jdId] = first
  return first
}

/**
 * Everything a benchmark was built from, as one short string.
 *
 * **A second copy of `fingerprintOf` in backend/src/lib/freshness.ts, because
 * the frontend does not import backend code. Change both.**
 *
 * It exists here because without it the mock's "what has changed" was the
 * role's version and nothing else, so adding a survey re-benchmarked nothing
 * and no range could ever have a figure waiting on it. The real rule has
 * always counted market data as a change; the mock quietly did not, which is
 * the third time a mock disagreeing with a rule has hidden behaviour in this
 * codebase.
 */
const fingerprintOf = (jd: JobDescription): string => `${jd.version}.${marketStamp()}`

/** Whether this role has to be matched again. The same three reasons the real one has. */
const needsMatching = (jd: JobDescription) => {
  const bench = (db.benchmarks ?? {})[jd.id]
  if (!bench) return true
  if (bench.failedReason) return true
  return bench.fingerprint !== fingerprintOf(jd)
}

export const mockApi: Api = {
  async login(email, password) {
    await wait(420)
    const at = email.trim().toLowerCase()
    // The store is looked at first, so a password set on the companies screens
    // is the one that works. Checking the built in list first would leave a
    // reset looking as though it had done nothing.
    const made = findLogin(at)
    const built = made ? undefined : USERS[at]
    if (built) {
      // Never reveal whether the address exists (FR-A1).
      if (built.password !== password) {
        throw new ApiError(401, 'That email address and password do not match.')
      }
      sessionStorage.setItem(SESSION_KEY, at)
      return { kind: 'signedIn', me: withCounts(built.me) }
    }

    if (!made || made.password !== password || made.disabled) {
      throw new ApiError(401, 'That email address and password do not match.')
    }

    // Still on the password a super admin handed out. Correct, and spent: the
    // next screen asks them to choose their own, exactly as the deployed
    // system does, so the offline build cannot make that screen look optional.
    if (made.temporary) {
      return {
        kind: 'newPasswordNeeded',
        challenge: {
          finish: async (chosen: string) => {
            await wait(300)
            const rule = Admin.weakPassword(chosen)
            if (rule) throw new ApiError(400, rule)
            made.password = chosen
            made.temporary = false
            save()
            sessionStorage.setItem(SESSION_KEY, at)
            // The screen calls finishNewPassword, which reads the session, so
            // the tokens here are only a shape. Nothing offline has a token.
            return { accessToken: 'mock', refreshToken: 'mock' }
          },
        },
      }
    }

    sessionStorage.setItem(SESSION_KEY, at)
    const me = madeUser(at)
    if (!me) throw new ApiError(401, 'That email address and password do not match.')
    return { kind: 'signedIn', me: withCounts(me) }
  },

  async finishNewPassword(challenge, password) {
    await challenge.finish(password)
    // The challenge set the password and left the session behind it.
    return session()
  },

  async me() {
    await wait(120)
    return session()
  },

  async logout() {
    sessionStorage.removeItem(SESSION_KEY)
    await wait(80)
  },

  async listJobDescriptions({ q = '', cursor = null, page: asked, limit = 100 }) {
    await wait()
    session()
    const all = roles()
    const w = words(q)
    const items = all.filter((j) => matches(j, w)).sort(byNewest)
    // A page by number, as the real API answers the screen (FR-2b).
    if (asked !== undefined) {
      const pages = Math.max(1, Math.ceil(items.length / limit))
      const at = Math.min(Math.max(0, asked), pages - 1)
      return {
        items: items.slice(at * limit, at * limit + limit),
        cursor: null,
        total: all.length,
        matched: items.length,
        page: at,
        pages,
      }
    }
    /*
     * The real API hands back an opaque pointer naming the last role of the
     * page, not a count of rows to skip, so the stand-in does the same.
     * Swapping in the real backend must not change the screen, and a count
     * into a list that is rebuilt on every write skips and repeats roles.
     */
    const mark = decodeCursor(cursor)
    const from = mark ? items.findIndex((e) => isAfter(e, mark)) : 0
    const page = from < 0 ? [] : items.slice(from, from + limit)
    const last = page[page.length - 1]
    const more = from >= 0 && from + page.length < items.length
    return {
      items: page,
      cursor: more && last ? encodeCursor({ updated: last.updated, id: last.id }) : null,
      total: all.length,
      matched: items.length,
    }
  },

  async getJobDescription(jdId) {
    await wait()
    session()
    const jd = roles().find((j) => j.id === jdId)
    if (!jd) throw new ApiError(404, 'That job description no longer exists.')
    return jd
  },

  async getVersions(jdId) {
    await wait(140)
    session()
    return db.versions[jdId] ?? []
  },

  async createJobDescription(draft, status) {
    await wait(380)
    const me = session()
    requireAdmin(me)
    const fields = draftToFields(draft)
    if (!fields.title) throw new ApiError(400, 'Give the role a title before saving.')
    const sections = sectionsSent(draft.sections)
    const jd: JobDescription = {
      id: id('jd-'),
      ...fields,
      family: draft.family,
      industry: draft.industry?.trim() || industryFor(draft.family),
      // The role's sections, from the document or typed into the form.
      ...(sections ? { sections } : {}),
      status,
      version: 1,
      updated: today(),
      createdBy: me.name,
      updatedBy: me.name,
    }
    db.jds = [jd, ...db.jds]
    db.versions[jd.id] = [{ version: 1, date: today(), note: 'Created', by: me.name }]
    save()
    // Read again unless the hidden fields already describe these sections
    // (FR-50a): an upload says what it read them from, and anything else is
    // described by the sections its own fields make.
    if (sections && !sameSections(sections, draft.fieldsReadFrom ?? builtSections(fields))) {
      queueRefresh(jd.id, sections)
    }
    return jd
  },

  async updateJobDescription(jdId, draft, status) {
    await wait(380)
    const me = session()
    requireAdmin(me)
    const jd = roles().find((j) => j.id === jdId)
    if (!jd) throw new ApiError(404, 'That job description no longer exists.')
    // An edit that does not name a hidden field keeps what the role has,
    // and one that sends no sections keeps its sections, as the route does.
    const fields: Partial<JobDescription> = draftToFields(draft)
    for (const name of ['summary', 'responsibilities', 'skills', 'quals'] as const) {
      if (!(name in draft)) delete fields[name]
    }
    const sections = sectionsSent(draft.sections)
    const next: JobDescription = {
      ...jd,
      ...fields,
      ...(sections ? { sections } : {}),
      ...(draft.family?.trim() ? { family: draft.family.trim() } : {}),
      ...(draft.industry?.trim() ? { industry: draft.industry.trim() } : {}),
      version: jd.version + 1,
      status,
      updated: today(),
      updatedBy: me.name,
    }
    db.jds = db.jds.map((j) => (j.id === jdId ? next : j))
    db.versions[jdId] = [
      {
        version: next.version,
        date: today(),
        note: status === 'Draft' ? 'Edited, kept as a draft' : 'Edited and published',
        by: me.name,
      },
      ...(db.versions[jdId] ?? []),
    ]
    save()
    // Only when the text changed; key details alone read nothing again.
    if (sections && !sameSections(sections, sectionsOf(jd))) queueRefresh(jdId, sections)
    return next
  },

  async deleteJobDescription(jdId) {
    await wait(420)
    const me = session()
    requireAdmin(me)
    const jd = roles().find((j) => j.id === jdId)
    if (!jd) throw new ApiError(404, 'That job description no longer exists.')

    // Four things have to go, not one. Leaving the version history behind fills
    // the table with records nothing can reach. Leaving a change request behind
    // leaves the approvals queue pointing at a role that is not there any more,
    // and the waiting count has to come down with it.
    delete db.versions[jdId]
    const orphaned = db.requests.filter((r) => r.jdId === jdId)
    db.requests = db.requests.filter((r) => r.jdId !== jdId)
    db.jds = db.jds.filter((j) => j.id !== jdId)
    save()
    void orphaned
  },

  async exportCsv(q) {
    await wait(200)
    session()
    const list = roles()
      .filter((j) => matches(j, words(q)))
      .sort((a, b) => (a.updated < b.updated ? 1 : -1))
    const scope = q.trim() ? 'search' : 'all'
    return { fileName: `job-descriptions-${scope}-${today()}.csv`, body: jdsToCsv(list) }
  },

  async exportDocument(jdId) {
    await wait(200)
    session()
    const jd = roles().find((j) => j.id === jdId)
    if (!jd) throw new ApiError(404, 'That job description no longer exists.')
    const name = jd.title.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase()
    return {
      fileName: `${name}-v${jd.version}.txt`,
      body: jdToDocument(jd, db.versions[jdId] ?? []),
    }
  },

  async startExtraction(file, text) {
    await wait(200)
    const me = session()
    requireAdmin(me)
    // The real route refuses a spreadsheet at presign, so the stand-in has to
    // as well. It was accepting one and reading it as a single role.
    const name = file?.name ?? ''
    if (/\.(csv|tsv|xlsx|xls)$/i.test(name)) {
      throw new ApiError(400, `“${name}” is a spreadsheet. Use mass upload for it, which is ` +
        'faster and reads every row exactly as written.')
    }
    if (name && !/\.(pdf|docx|txt|md|text)$/i.test(name)) {
      throw new ApiError(400, `A document takes a .pdf, .docx or .txt file, and “${name}” is not one.`)
    }
    const job: UploadJob & { text?: string; startedAt?: number } = {
      id: id('up-'),
      kind: 'single',
      fileName: file?.name ?? 'pasted text',
      status: 'extracting',
      draft: null,
      error: null,
      createdAt: today(),
      text,
      startedAt: Date.now(),
    }
    db.uploads[job.id] = job
    save()
    return job
  },

  async startBulkCheck(text, fileName, sheet) {
    await wait(320)
    const me = session()
    requireAdmin(me)
    // An Excel file is bytes, so it is turned into the same lines a .csv is and
    // everything after this is shared. The stand-in has to take one because the
    // product does.
    let source = text
    if (sheet) {
      try {
        source = await xlsxToCsv(await sheet.arrayBuffer())
      } catch (e) {
        throw new ApiError(400, `${fileName} could not be read as an Excel file. ${(e as Error).message}`)
      }
    }
    const raw = source.replace(/\r\n?/g, '\n').trim()
    if (!raw) throw new ApiError(400, 'Choose a CSV file or paste rows first.')
    let lines = raw.split('\n').filter((l) => l.trim())
    if (/^\s*"?title"?\s*,/i.test(lines[0])) lines = lines.slice(1)
    if (!lines.length) throw new ApiError(400, 'That file has a header row but no job descriptions.')
    if (!holdsBulkRows(lines)) {
      throw new ApiError(
        400,
        `Nothing in that file is a row of job description data. The spreadsheet has ` +
        `${BULK_COLUMNS.length} columns in this order: ${BULK_COLUMNS.join(', ')}. ` +
        'Use the example on this screen to see the format. If this is a job description ' +
        'written out in words rather than a spreadsheet, use Add job description, which ' +
        'reads documents.',
      )
    }

    const seen = new Map<string, number>()
    for (const j of roles()) seen.set(slug(j.title), (seen.get(slug(j.title)) ?? 0) + 1)
    const rows = lines.map((line, i) => validateBulkRow(line, i, seen, usedFamilies()))

    const job: UploadJob = {
      id: id('up-'),
      kind: 'bulk',
      fileName: fileName || 'pasted rows',
      status: 'validated',
      rows,
      // The screen waits until every row has a verdict, so the mock reports the
      // same total the real backend does even though it checks them all at once.
      rowCount: rows.length,
      summary: {
        ready: rows.filter((r) => !r.errors.length).length,
        errors: rows.filter((r) => r.errors.length).length,
      },
      error: null,
      createdAt: today(),
    }
    db.uploads[job.id] = job
    save()
    return job
  },

  /**
   * A document of many roles, faked.
   *
   * There is no model here, so this returns a fixed set shaped to show every
   * state the real one can produce: rows that are ready, rows held back
   * because the document never said which family or level they were, a
   * placeholder the reader refused to import, and a title the library already
   * has. Without this the four states could only be seen against real AWS,
   * which is how the last round of screen bugs reached a deployed environment.
   */
  async startDocsetCheck(file) {
    await wait(700)
    const me = session()
    requireAdmin(me)
    if (!/\.(pdf|docx|txt)$/i.test(file.name)) {
      throw new ApiError(400, `A document upload takes a .pdf, .docx or .txt file, and “${file.name}” is not one.`)
    }
    const ladder = /ladder|matrix|grid/i.test(file.name)
    const seen = new Map<string, number>()
    for (const j of roles()) seen.set(slug(j.title), (seen.get(slug(j.title)) ?? 0) + 1)

    const sample = ladder ? MOCK_MATRIX_ROLES : MOCK_DOC_ROLES
    const rows: BulkRow[] = sample.map((r, i) => {
      const errors: string[] = []
      if (!r.family) errors.push('no job family in the document, choose one before importing')
      if (!r.level) errors.push('no level in the document, choose one before importing')
      const shared = seen.get(slug(r.title)) ?? 0
      return {
        ...r, line: i + 1, from: 'model', errors,
        ...(shared > 0 ? { sameTitle: shared } : {}),
      }
    })

    const job: UploadJob = {
      id: id('up-'),
      kind: 'docset',
      fileName: file.name,
      status: 'validated',
      rows,
      rowCount: rows.length,
      docset: {
        kind: ladder ? 'level_matrix' : 'job_descriptions',
        note: ladder
          ? 'The document is a grid of levels against responsibility categories, with no prose job description per role.'
          : 'Each page contains a distinct role with its own overview, responsibilities and requirements.',
        pagesTotal: rows.length,
        pagesDone: rows.length,
      },
      summary: {
        ready: rows.filter((r) => !r.errors.length).length,
        errors: rows.filter((r) => r.errors.length).length,
      },
      error: null,
      createdAt: today(),
    }
    db.uploads[job.id] = job
    save()
    return job
  },

  async getUpload(uploadId) {
    await wait(120)
    session()
    const job = db.uploads[uploadId]
    if (!job) throw new ApiError(404, 'That upload no longer exists.')
    // Single-document extraction resolves after a few seconds, which is what
    // gives the progress screen something real to show.
    // A survey becomes readable a moment after it was started, which is the
    // shortest honest stand-in for a Lambda that takes a few seconds. Either
    // way the screen has to poll for it.
    if (job.kind === 'market' && job.status === 'extracting') {
      if (Date.now() - (job.startedAt ?? 0) > 1200) {
        job.status = 'validated'
        save()
      }
    }
    if (job.kind === 'single' && job.status === 'extracting') {
      const elapsed = Date.now() - (job.startedAt ?? 0)
      if (elapsed > 4500) {
        const text = job.text ?? ''
        if (!text.trim()) {
          job.status = 'failed'
          job.error = /\.pdf$/i.test(job.fileName)
            ? `PDFs are read by Claude on the server, which is not built yet (STE-544). Until then, open ${job.fileName}, copy the text, and paste it below.`
            : `Nothing readable came back from ${job.fileName}. Open it, copy the text, and paste it below instead.`
        } else if (rolesIn(text) > 1) {
          // The real reader counts the roles in the document and refuses a set,
          // because this page adds one and reading the first of twelve while
          // saying nothing about the rest is the worst available answer.
          job.status = 'failed'
          job.error =
            `This file holds ${rolesIn(text)} job descriptions and this page adds one. ` +
            'Use mass upload for it, which reads every role in the file and lets you check ' +
            'each one before importing. Nothing has been saved.'
        } else {
          const draft = extractDraft(text)
          // Same rule as the real reader and as mass upload: a role has a title
          // and it has responsibilities or requirements of its own. A source
          // file saved as .txt has neither, and used to come back ready with an
          // empty form.
          const substance = Boolean(draft.responsibilities.trim() || draft.quals.trim())
          if (!draft.title.trim() || !substance) {
            job.status = 'failed'
            job.error =
              'No job description could be read from that file. It needs a job title and ' +
              'either responsibilities or requirements. Check the file is the right one, or ' +
              'paste the text of the role into the box instead.'
          } else {
            job.status = 'ready'
            // The document's own sections ride with the draft, as the real
            // extraction returns them (FR-42).
            const sections = sectionsFromText(text)
            job.draft = sections ? { ...draft, sections } : draft
          }
        }
        save()
      }
    }
    return job
  },

  async setOnRows(uploadId, patch) {
    await wait(200)
    const me = session()
    requireAdmin(me)
    const job = db.uploads[uploadId]
    if (!job) throw new ApiError(404, 'That upload no longer exists.')
    if (!patch.family && !patch.level) {
      throw new ApiError(400, 'Choose a job family or a level to set.')
    }
    const wanted = patch.lines?.length ? new Set(patch.lines) : null
    const seen = new Map<string, number>()
    for (const j of roles()) seen.set(slug(j.title), (seen.get(slug(j.title)) ?? 0) + 1)

    job.rows = (job.rows ?? []).map((r) => {
      const touched = !wanted || wanted.has(r.line)
      const family = touched && !r.family ? (patch.family ?? '') : r.family
      const level = touched && !r.level ? (patch.level ?? '') : r.level
      const errors: string[] = []
      if (!family) errors.push('no job family in the document, choose one before importing')
      if (!level) errors.push('no level in the document, choose one before importing')
      const shared = seen.get(slug(r.title)) ?? 0
      return { ...r, family, level, errors, ...(shared > 0 ? { sameTitle: shared } : {}) }
    })
    job.summary = {
      ready: job.rows.filter((r) => !r.errors.length).length,
      errors: job.rows.filter((r) => r.errors.length).length,
    }
    save()
    return job
  },

  async editRow(uploadId, line, set, sections) {
    await wait(160)
    requireAdmin(session())
    const job = db.uploads[uploadId]
    if (!job) throw new ApiError(404, 'That upload no longer exists.')
    const seen = new Map<string, number>()
    for (const j of roles()) seen.set(slug(j.title), (seen.get(slug(j.title)) ?? 0) + 1)

    job.rows = (job.rows ?? []).map((r) => {
      if (r.line !== line) return r
      const next = {
        ...r,
        ...Object.fromEntries(
          Object.entries(set).map(([k, v]) =>
            [k, k === 'payMin' || k === 'payMax' ? parseFloat(v) || 0 : v]),
        ),
      } as BulkRow
      const decided = new Set(Object.keys(set))
      const guessed = (r.guessed ?? []).filter((f) => !decided.has(f))
      const errors: string[] = []
      if (!next.title.trim()) errors.push('title is required')
      if (r.from === 'model') {
        if (!next.family) errors.push('no job family worked out for this role, set one before importing')
        if (!next.level) errors.push('no level worked out for this role, set one before importing')
      }
      // A spreadsheet cell nobody has touched is still measured against the
      // families the company uses. Editing the level on a row whose family was
      // wrong used to drop the family message, so the row went green with the
      // bad value still in it.
      if (r.from === 'csv' && !decided.has('family') && next.family && !usedFamilies().includes(next.family)) {
        errors.push(`unknown job family "${next.family}"`)
      }
      if (next.payMin && next.payMax && next.payMin > next.payMax) {
        errors.push(`pay min ${next.payMin} is higher than pay max ${next.payMax}`)
      }
      const shared = seen.get(slug(next.title)) ?? 0
      // The panel sends sections only when they changed (FR-59).
      const text = sections && !sameSections(sections, rowSections(r)) ? { sections } : {}
      return {
        ...next, ...text, errors,
        guessed: guessed.length ? guessed : undefined,
        ...(shared > 0 ? { sameTitle: shared } : {}),
      }
    })
    job.summary = {
      ready: job.rows.filter((r) => !r.errors.length).length,
      errors: job.rows.filter((r) => r.errors.length).length,
    }
    save()
    return job
  },

  // Answers the same shape the real one does, including `done`, or the loop
  // that drives a big import would never stop.
  async commitUpload(uploadId) {
    await wait(500)
    const me = session()
    requireAdmin(me)
    const job = db.uploads[uploadId]
    if (!job) throw new ApiError(404, 'That upload no longer exists.')
    // Pressing import twice must not create two copies (FR-14).
    if (job.status === 'imported') return { imported: 0, done: true }
    const all = (job.rows ?? []).filter((r) => !r.errors.length)
    if (!all.length) throw new ApiError(400, 'No rows are ready to import. Fix the flagged lines and check again.')

    // Sliced like the real one, so the 5,000 row dev file exercises the loop
    // that drives a big import rather than a shortcut only the mock has.
    const from = job.importCursor ?? 0
    const rows = all.slice(from, from + MOCK_IMPORT_SLICE)
    const stamp = today()
    const created: JobDescription[] = rows.map((r) => ({
      id: id('jd-'),
      title: r.title,
      family: r.family,
      level: r.level as Level,
      location: r.location,
      payMin: r.payMin,
      payMax: r.payMax,
      ...rowText(r),
      status: r.status as JdStatus,
      version: 1,
      updated: stamp,
      industry: r.industry || industryFor(r.family),
      // Every section, as the real import carries them (FR-57, FR-59).
      ...(r.sections?.length ? { sections: r.sections } : {}),
      createdBy: me.name,
      updatedBy: me.name,
    }))
    for (const jd of created) {
      db.versions[jd.id] = [{ version: 1, date: stamp, note: 'Imported from a spreadsheet', by: me.name }]
    }
    db.jds = [...created, ...db.jds]
    job.importCursor = from + rows.length
    const done = job.importCursor >= all.length
    if (done) job.status = 'imported'
    save()
    return { imported: created.length, done, position: job.importCursor, total: all.length }
  },

  async createChangeRequest({ jdId, reason, changes, baseVersion }) {
    await wait(380)
    const me = session()
    const jd = roles().find((j) => j.id === jdId)
    if (!jd) throw new ApiError(404, 'That job description no longer exists.')
    if (!reason.trim()) throw new ApiError(400, 'Add a short reason before sending for approval.')
    if (!Object.keys(changes).length) throw new ApiError(400, 'Nothing changed yet.')
    const req: ChangeRequest = {
      id: id('cr-'),
      jdId,
      jdTitle: jd.title,
      requestedBy: me.name,
      requestedByRole: 'People manager',
      requestedAt: today(),
      reason: reason.trim(),
      changes: changes as Partial<Record<ProposableField, ChangeValue>>,
      baseVersion,
      status: 'pending',
    }
    db.requests = [req, ...db.requests]
    db.jds = db.jds.map((j) => (j.id === jdId ? { ...j, status: 'In review' as const } : j))
    save()
    return req
  },

  async listChangeRequests() {
    await wait(180)
    const me = session()
    // A manager sees only their own (FR-18).
    return me.role === 'admin'
      ? db.requests
      : db.requests.filter((r) => r.requestedBy === me.name)
  },

  async approveChangeRequest(crId) {
    await wait(420)
    const me = session()
    requireAdmin(me)
    const req = db.requests.find((r) => r.id === crId)
    if (!req) throw new ApiError(404, 'That request no longer exists.')
    const jd = roles().find((j) => j.id === req.jdId)
    if (!jd) throw new ApiError(404, 'That job description no longer exists.')
    // FR-23: the role moved on since the request was raised, so approving would
    // silently overwrite whoever edited it. The screen checks this on load too;
    // this is the check that actually enforces it.
    if (isStale(jd, req)) throw new ApiError(409, staleMessage(jd, req))
    const stamp = today()
    const nextVersion = jd.version + 1
    const updated: JobDescription = {
      ...jd,
      ...(req.changes as Partial<JobDescription>),
      version: nextVersion,
      updated: stamp,
      status: 'Published',
      updatedBy: me.name,
    }
    db.requests = db.requests.map((r) =>
      r.id === crId ? { ...r, status: 'approved' as const, decidedBy: me.name, decidedAt: stamp } : r,
    )
    const stillPending = db.requests.some((r) => r.status === 'pending' && r.jdId === req.jdId)
    db.jds = db.jds.map((j) =>
      j.id === jd.id ? { ...updated, status: stillPending ? 'In review' : 'Published' } : j,
    )
    db.versions[jd.id] = [
      { version: nextVersion, date: stamp, note: `Approved change from ${req.requestedBy}`, by: me.name },
      ...(db.versions[jd.id] ?? []),
    ]
    save()
    // An approved change to the sections is read again like an edit (FR-50).
    const proposed = req.changes.sections as DocSection[] | undefined
    if (proposed?.length && !sameSections(proposed, sectionsOf(jd))) queueRefresh(jd.id, proposed)
  },

  async rejectChangeRequest(crId, note) {
    await wait(320)
    const me = session()
    requireAdmin(me)
    if (!note.trim()) throw new ApiError(400, 'Add a comment so the requester knows why.')
    const req = db.requests.find((r) => r.id === crId)
    if (!req) throw new ApiError(404, 'That request no longer exists.')
    db.requests = db.requests.map((r) =>
      r.id === crId
        ? { ...r, status: 'rejected' as const, decisionNote: note.trim(), decidedBy: me.name, decidedAt: today() }
        : r,
    )
    const stillPending = db.requests.some((r) => r.status === 'pending' && r.jdId === req.jdId)
    if (!stillPending) {
      db.jds = db.jds.map((j) => (j.id === req.jdId && j.status === 'In review' ? { ...j, status: 'Published' } : j))
    }
    save()
  },

  // ---------------------------------------------------------------------------
  // Market data. Every method here refuses a manager, exactly as the API does.
  // ---------------------------------------------------------------------------

  async listMarketData({ signal, ...params }) {
    const slow = slowMarket()
    await waitOr(signal, slow ? slowFor(slow.ms, params.q) : LATENCY)
    requireAdmin(session())
    return queryMarket(db.market, params, slow?.reads)
  },

  async getMarketRecord(id) {
    await wait()
    requireAdmin(session())
    const record = db.market.records.find((r) => r.id === id)
    if (!record) throw new ApiError(404, 'That market record no longer exists.')
    const survey = db.market.surveys.find((s) => s.id === record.survey)
    if (!survey) throw new ApiError(404, 'That survey no longer exists.')
    // The columns travel with the record, because the values are keyed by
    // column key and mean nothing without their headings.
    return { record, survey }
  },

  async updateMarketRecord(id, values) {
    await wait(320)
    const me = session()
    requireAdmin(me)
    const record = db.market.records.find((r) => r.id === id)
    if (!record) throw new ApiError(404, 'That market record no longer exists.')
    const survey = db.market.surveys.find((s) => s.id === record.survey)
    if (!survey) throw new ApiError(404, 'That survey no longer exists.')

    const known = new Set(survey.columns.map((c) => c.key))
    for (const key of Object.keys(values)) {
      if (!known.has(key)) throw new ApiError(400, `This survey has no column "${key}".`)
    }
    if (!Object.keys(values).length) throw new ApiError(400, 'Nothing to change.')

    // Whatever is typed is what is stored. There is nothing to check it
    // against: the module does not know what a column means.
    Object.assign(record.values, values)
    record.updatedBy = me.name
    record.updatedAt = new Date().toISOString()
    save()
    return record
  },

  async deleteMarketRecord(id) {
    await wait(320)
    requireAdmin(session())
    const record = db.market.records.find((r) => r.id === id)
    if (!record) throw new ApiError(404, 'That market record no longer exists.')
    db.market.records = db.market.records.filter((r) => r.id !== id)

    const survey = db.market.surveys.find((s) => s.id === record.survey)
    let surveyRemoved = false
    if (survey) {
      survey.rowCount = db.market.records.filter((r) => r.survey === survey.id).length
      // A survey holding nothing is a line on a screen that makes people
      // wonder what happened to it.
      if (survey.rowCount === 0) {
        db.market.surveys = db.market.surveys.filter((s) => s.id !== survey.id)
        surveyRemoved = true
      }
    }
    save()
    return { surveyRemoved }
  },

  /* ---------- benchmarking ---------- */

  /*
   * A stand-in that is not kinder than the real thing.
   *
   * It refuses what the API refuses, in the same order and with the same
   * messages: a manager, a company with no survey, an empty choice, a code
   * this benchmark never considered. A mock that says yes where the product
   * says no is a mock that hides the screen somebody actually sees.
   */

  async benchmarkOverview(params) {
    await wait()
    requireAdmin(session())
    const hasSurvey = db.market.surveys.length > 0
    const going = liveRun()
    const q = (params?.q ?? '').trim().toLowerCase()
    const limit = params?.limit ?? 100
    const marks = db.benchmarks ?? {}
    let benchmarked = 0, outOfDate = 0, neverRun = 0, noMatch = 0
    const all = db.jds.map((jd) => {
      const bench = marks[jd.id]
      let status: BenchmarkStatus
      if (!bench) { status = 'never run'; neverRun += 1 }
      else if (bench.jdVersion !== jd.version) { status = 'out of date'; outOfDate += 1 }
      else if (bench.noMatchReason !== null) { status = 'no match found'; noMatch += 1 }
      else { status = bench.position as BenchmarkStatus; benchmarked += 1 }
      const current = bench && bench.jdVersion === jd.version
      return {
        jdId: jd.id, title: jd.title, family: jd.family, level: jd.level, status,
        location: jd.location, payMin: jd.payMin, payMax: jd.payMax,
        compaRatio: current ? bench!.compaRatio : null,
        confidence: current ? bench!.confidence : null,
        runAt: bench?.runAt ?? null,
      }
    })
    /*
     * Searched, ordered and sliced here, because the server does it there.
     * A stand-in that paged differently would pass a screen that is wrong
     * against the real thing, which is the one job a stand-in must not do.
     *
     * Every word has to match, across the same fields the library searches.
     */
    const words = q.split(/\s+/).filter(Boolean)
    const matched = all
      .filter((row) => {
        if (!words.length) return true
        const jd = db.jds.find((j) => j.id === row.jdId)
        const text = [
          row.title, row.family, row.level, row.location, jd?.status, jd?.summary,
          ...(jd?.skills ?? []), ...(jd?.responsibilities ?? []),
        ].join(' ').toLowerCase()
        return words.every((w) => text.includes(w))
      })
      .sort((a, b) => {
        const au = db.jds.find((j) => j.id === a.jdId)?.updated ?? ''
        const bu = db.jds.find((j) => j.id === b.jdId)?.updated ?? ''
        return au === bu ? (a.jdId < b.jdId ? 1 : -1) : (au < bu ? 1 : -1)
      })
    // Numbered, because the server can serve any page directly: the whole
    // library is in hand already to answer the counts.
    const pages = Math.max(1, Math.ceil(matched.length / limit))
    const at = Math.min(Math.max(0, params?.page ?? 0), pages - 1)
    const items = matched.slice(at * limit, at * limit + limit)

    return {
      hasSurvey,
      page: at,
      pages,
      total: all.length,
      matched: matched.length,
      // The stand-in has to answer this the way the server does, or the screen
      // harness passes a banner that never appears against the real thing.
      running: going === undefined ? null : {
        runId: going.runId,
        state: going.state,
        done: going.done,
        total: going.total,
        secondsLeft: Math.max(0, going.total - going.done) * SECONDS_EACH,
        startedBy: going.startedBy,
        startedAt: going.startedAt,
      },
      counts: {
        benchmarked, outOfDate, neverRun, noMatch,
        // The same rule the real one uses: a no match is a finished answer.
        done: benchmarked + noMatch,
        toDo: neverRun + outOfDate,
      },
      items,
    }
  },

  async benchmarkEstimate(scope) {
    await wait()
    requireAdmin(session())
    const ids = db.jds.filter(needsMatching).map((jd) => jd.id)
    return { scope, count: ids.length, seconds: ids.length * SECONDS_EACH }
  },

  async startBenchmarkRun(scope) {
    void scope
    await wait()
    requireAdmin(session())
    const open = liveRun()
    if (open) return { runId: open.runId, count: open.total, alreadyRunning: true }
    if (db.market.surveys.length === 0) {
      throw new ApiError(409, 'There is no market data to compare against yet. Add a survey first.')
    }
    /*
     * Both scopes mean the same thing, as they do in the real one: match what
     * needs matching. Asking an agent to repeat itself is never the answer, so
     * a run over unchanged data would move a pay band with nothing behind it.
     */
    const ids = db.jds.filter(needsMatching).map((jd) => jd.id)
    if (ids.length === 0) {
      throw new ApiError(409, 'Every job description is already benchmarked against the current version.')
    }
    const runId = `run_${Date.now().toString(36)}`
    db.runs = db.runs ?? {}
    db.runs[runId] = {
      runId, state: 'running', total: ids.length, done: 0, failed: 0, throttled: 0, waits: 0, noMatch: 0,
      secondsLeft: ids.length * SECONDS_EACH, recent: [],
      startedBy: session()?.name ?? 'You', startedAt: new Date().toISOString(), finishedAt: null,
    }
    db.pendingRuns = db.pendingRuns ?? {}
    db.pendingRuns[runId] = ids
    save()
    return { runId, count: ids.length }
  },

  async benchmarkOneRole(jdId) {
    await wait()
    requireAdmin(session())
    const open = liveRun()
    if (open) return { runId: open.runId, count: open.total, alreadyRunning: true }
    if (db.market.surveys.length === 0) {
      throw new ApiError(409, 'There is no market data to compare against yet. Add a survey first.')
    }
    if (!db.jds.some((jd) => jd.id === jdId)) throw new ApiError(404, 'Not found.')

    // Asked for by name, so it runs whether or not it is current. Answering
    // "already up to date" refuses the thing the person asked for.
    const runId = `run_${Date.now().toString(36)}`
    db.runs = db.runs ?? {}
    db.runs[runId] = {
      runId, state: 'running', total: 1, done: 0, failed: 0, throttled: 0, waits: 0, noMatch: 0,
      secondsLeft: 20, recent: [],
      startedBy: session()?.name ?? 'You', startedAt: new Date().toISOString(), finishedAt: null,
    }
    db.pendingRuns = db.pendingRuns ?? {}
    db.pendingRuns[runId] = [jdId]
    save()
    return { runId, count: 1 }
  },

  async benchmarkRun(runId) {
    await wait()
    requireAdmin(session())
    const run = (db.runs ?? {})[runId]
    if (!run) throw new ApiError(404, 'That run is not there.')

    // One role per poll, so the progress screen can actually be watched
    // rather than jumping from nothing to finished.
    const waiting = (db.pendingRuns ?? {})[runId] ?? []
    if (run.state === 'running' && waiting.length > 0) {
      const jdId = waiting.shift()!
      const jd = db.jds.find((j) => j.id === jdId)
      if (jd) {
        const bench = makeBenchmark(jd)
        // What it was built from, so the next run can tell whether anything
        // has moved rather than asking again over unchanged data.
        bench.fingerprint = fingerprintOf(jd)
        db.benchmarks = db.benchmarks ?? {}
        db.benchmarks[jd.id] = bench
        run.done += 1
        if (bench.noMatchReason !== null) run.noMatch += 1
        run.recent = [{
          jdId: jd.id, title: jd.title,
          outcome: bench.noMatchReason !== null ? 'no match found' : bench.position,
          compaRatio: bench.compaRatio,
        }, ...run.recent].slice(0, 12)
      } else {
        run.done += 1
      }
      run.secondsLeft = Math.max(run.total - run.done, 0) * SECONDS_EACH
    }
    if (waiting.length === 0 || run.state === 'stopping') {
      run.state = run.state === 'stopping' || waiting.length > 0 ? 'stopped' : 'done'
      run.finishedAt = run.finishedAt ?? new Date().toISOString()
    }
    save()
    return { ...run }
  },

  async stopBenchmarkRun(runId) {
    await wait()
    requireAdmin(session())
    const run = (db.runs ?? {})[runId]
    if (!run) throw new ApiError(404, 'That run is not there.')
    if (run.state !== 'running') return { state: run.state }
    run.state = 'stopping'
    save()
    return { state: 'stopping' as const }
  },

  /*
   * HR feedback (STE-991). The same refusals as the server, in the same words,
   * and a re-match that moves one step each time the screen checks on it, so
   * the progress line can be watched in mock mode.
   */
  async askFeedback(jdId, ask) {
    await wait()
    const me = session()
    requireAdmin(me)
    const problem = reasonHint(ask.reason ?? '')
    if (problem) throw new ApiError(400, problem)
    const bench = (db.benchmarks ?? {})[jdId]
    if (!db.jds.some((j) => j.id === jdId)) throw new ApiError(404, 'Not found.')
    if (!bench || bench.failedReason) throw new ApiError(409, 'Benchmark this role first.')
    db.feedback = db.feedback ?? {}
    const held = db.feedback[jdId] ?? { pending: null, history: [] }
    const waiting = held.pending && ['queued', 'matching', 'ready'].includes(held.pending.state)
    if (waiting) throw new ApiError(409, 'A re-match for this role is already waiting. Decide on it, or wait for it to finish.')
    if (bench.confirmed && ask.replaceConfirmed !== true) {
      throw new ApiError(409, 'This match was picked by hand. Matching again may replace it, so say you want to go ahead.')
    }
    const at = new Date().toISOString()
    const pending: PendingFeedback = {
      feedbackId: id('fb_'), jdId, reason: ask.reason.trim(),
      ...(ask.surveyJob ? { surveyJob: ask.surveyJob.trim() } : {}),
      by: { sub: me.email, name: me.name }, at, state: 'queued',
      base: { runAt: bench.runAt, fingerprint: bench.fingerprint ?? '', chosenCodes: bench.cuts.filter((c) => c.chosen).map((c) => c.code) },
    }
    held.pending = pending
    held.history = [{
      feedbackId: pending.feedbackId, jdId, reason: pending.reason, surveyJob: pending.surveyJob,
      by: pending.by, at, before: pending.base.chosenCodes, decision: 'waiting',
    }, ...held.history]
    db.feedback[jdId] = held
    save()
    return { feedbackId: pending.feedbackId }
  },

  async getFeedback(jdId) {
    await wait()
    requireAdmin(session())
    if (!db.jds.some((j) => j.id === jdId)) throw new ApiError(404, 'Not found.')
    const held = (db.feedback ?? {})[jdId] ?? { pending: null, history: [] }
    const p = held.pending
    if (p && p.state === 'queued') p.state = 'matching'
    else if (p && p.state === 'matching') {
      // The mock's re-match: the next survey job on the Matches tab.
      const bench = (db.benchmarks ?? {})[jdId]
      if (bench && bench.noMatchReason !== null && bench.cuts.length === 0) {
        /*
         * A role that was not priced. The stand-in turns the suggestion down
         * and says why, because a suggested job is never a pairing: the role
         * stays not priced unless the matcher itself finds a fit.
         */
        const named = p.surveyJob ? `${p.surveyJob} was considered again with your reason. ` : ''
        const turnedDown = `${named}It still describes different work from this role, so the role stays not priced.`
        p.answer = { ...bench, noMatchReason: turnedDown, confirmed: undefined, feedbackAnswer: turnedDown }
        p.feedbackAnswer = turnedDown
        p.state = 'ready'
      } else if (bench) {
        // The match now is the chosen cut, or on an answer that never marked
        // one, the first that carries weight: the same rule the card reads by.
        const marked = bench.cuts.findIndex((c) => c.chosen === true)
        const now = marked >= 0 ? marked : bench.cuts.findIndex((c) => (c.weight ?? 0) > 0)
        const next = bench.cuts[(now + 1) % Math.max(bench.cuts.length, 1)]
        const answer: Benchmark = {
          ...bench,
          cuts: bench.cuts.map((c) => ({ ...c, chosen: c.code === next?.code })),
          band: next && next.p50 !== null
            ? { p25: next.p25 ?? null, p50: next.p50, p75: next.p75 ?? null, p90: next.p90 ?? null } as Benchmark['band']
            : bench.band,
          confirmed: undefined,
          feedbackAnswer: `Your reason was used: ${next?.title ?? 'the survey'} fits the scope you described better.`,
        }
        p.answer = answer
        p.feedbackAnswer = answer.feedbackAnswer
        p.state = 'ready'
      }
    }
    const jd = db.jds.find((j) => j.id === jdId)
    const stale = !!p && p.state !== 'failed' && !!jd && (db.benchmarks ?? {})[jdId]?.jdVersion !== jd.version
    save()
    return { pending: p ? { ...p, state: stale ? 'stale' : p.state } : null, history: held.history }
  },

  async keepFeedback(jdId, feedbackId) {
    await wait()
    const me = session()
    requireAdmin(me)
    const held = (db.feedback ?? {})[jdId]
    const p = held?.pending
    if (!held || !p || p.feedbackId !== feedbackId) throw new ApiError(404, 'Not found.')
    const bench = (db.benchmarks ?? {})[jdId]
    if (p.state !== 'ready' || !p.answer) throw new ApiError(409, 'There is no new answer to keep yet.')
    if (!bench || bench.runAt !== p.base.runAt) {
      throw new ApiError(409, 'The benchmark changed after you asked. Ask again, so you are choosing between what is there now.')
    }
    const codes = p.answer.cuts.filter((c) => c.chosen).map((c) => c.code).sort()
    const now = new Date().toISOString()
    const next: Benchmark = {
      ...p.answer, name: bench.name,
      confirmed: codes.length ? { codes, by: me.name, at: now } : undefined,
      runBy: me.name, runAt: now,
    }
    db.benchmarks![jdId] = next
    held.history = held.history.map((r) => r.feedbackId === feedbackId ? { ...r, decision: 'kept new', decidedAt: now, after: codes } : r)
    held.pending = null
    save()
    return next
  },

  async discardFeedback(jdId, feedbackId) {
    await wait()
    requireAdmin(session())
    const held = (db.feedback ?? {})[jdId]
    if (!held || !held.pending || held.pending.feedbackId !== feedbackId) throw new ApiError(404, 'Not found.')
    const now = new Date().toISOString()
    held.history = held.history.map((r) => r.feedbackId === feedbackId ? { ...r, decision: 'kept current', decidedAt: now } : r)
    held.pending = null
    save()
    return { kept: 'current' as const }
  },

  async getBenchmark(jdId) {
    await wait()
    requireAdmin(session())
    const bench = (db.benchmarks ?? {})[jdId]
    if (!bench) throw new ApiError(404, 'That job description has not been benchmarked yet.')
    const jd = db.jds.find((j) => j.id === jdId)
    return { ...bench, outOfDate: jd ? jd.version !== bench.jdVersion : false, jdTitle: jd?.title ?? '' }
  },

  /* ---------- salary ranges ---------- */

  /*
   * The rule is shared, not restated.
   *
   * `bandFor`, `accepted`, `waitingOn` and `spreadFor` are the same functions
   * the routes use. A mock that worked out a band its own way is a mock that
   * can agree with a wrong screen, and that has happened twice in this
   * codebase already.
   */
  async salaryRanges(opts = {}) {
    await wait()
    requireAdmin(session())
    const marks = db.benchmarks ?? {}
    const held = db.ranges ?? {}
    const spread = spreadIsSane(db.companySpread) ? db.companySpread : DEFAULT_SPREAD

    const items: RangeRow[] = db.jds.map((jd) => {
      const bench = marks[jd.id]
      const decision = held[jd.id]
      // Only the percentile this role sits on. A figure moving at one it is
      // not built on proposes nothing.
      const offered = offeredPcts(bench?.band)
      const pct = pctFor(decision?.percentile, offered)
      const market = figureAt(bench?.band, pct)
      const mine = decision ? { median: decision.median } : undefined
      const mid = accepted(mine, market)
      const waiting = waitingOn(mine, market)
      const width = spreadFor(spread, decision?.spread).spread
      const band = mid === null ? null : bandFor(mid, width)

      let state: RangeState = 'banded'
      if (!bench || bench.failedReason) state = 'never-run'
      else if (bench.noMatchReason !== null || !bench.band) state = 'no-match'
      else if (bench.jdVersion !== jd.version) state = 'out-of-date'

      return {
        jdId: jd.id, title: jd.title, family: jd.family, level: jd.level,
        location: jd.location, state,
        minimum: band?.minimum ?? null,
        midpoint: band?.midpoint ?? null,
        maximum: band?.maximum ?? null,
        currency: bench?.currency ?? null,
        unit: bench?.unit ?? null,
        spread: width,
        overridden: decision?.spread !== undefined,
        percentile: pct,
        percentiles: offered,
        confidence: bench?.confidence ?? null,
        waiting,
        acceptedBy: decision?.acceptedBy ?? null,
        acceptedAt: decision?.acceptedAt ?? null,
        outOfDate: Boolean(bench) && bench.jdVersion !== jd.version,
      }
    })

    /*
     * The same first-median rule the route keeps.
     *
     * Without it nothing can ever be waiting: a role with no decision reads
     * the market directly, so a later run would move its range silently.
     */
    db.ranges = db.ranges ?? {}
    let wrote = false
    for (const jd of db.jds) {
      if (db.ranges[jd.id]) continue
      const market = marks[jd.id]?.band?.p50 ?? null
      if (market === null) continue
      db.ranges[jd.id] = { jdId: jd.id, median: market, percentile: 'p50' }
      wrote = true
    }
    if (wrote) save()

    const banded = items.filter((i) => i.midpoint !== null).length

    /*
     * The same paging and the same order as the route: a role with a range
     * first, then the search, then one page of twenty. A mock that paged its
     * own way is a mock that can agree with a wrong screen.
     */
    const q = (opts.q ?? '').trim().toLowerCase()
    const family = (opts.family ?? '').trim()
    const matched = items.filter((r) => {
      if (opts.waiting && r.waiting === null) return false
      if (family && r.family !== family) return false
      if (!q) return true
      return `${r.title} ${r.level} ${r.location}`.toLowerCase().includes(q)
    })
    const inOrder = matched
      .map((row, at) => ({ row, at }))
      .sort((a, b) =>
        (Number(b.row.midpoint !== null) - Number(a.row.midpoint !== null)) || a.at - b.at)
      .map((x) => x.row)

    const limit = 20
    const pages = Math.max(1, Math.ceil(inOrder.length / limit))
    const at = Math.min(Math.max(0, opts.page ?? 0), pages - 1)

    return {
      hasSurvey: db.market.surveys.length > 0,
      spread,
      counts: { banded, unbanded: items.length - banded },
      waiting: items.filter((i) => i.waiting !== null).length,
      total: items.length,
      matched: matched.length,
      families: [...new Set(items.map((r) => r.family).filter(Boolean))].sort(),
      page: at,
      pages,
      items: inOrder.slice(at * limit, at * limit + limit),
    }
  },

  async salaryRange(jdId) {
    await wait()
    requireAdmin(session())
    const jd = db.jds.find((j) => j.id === jdId)
    if (!jd) throw new ApiError(404, 'That job description no longer exists.')

    const bench = (db.benchmarks ?? {})[jdId]
    const decision = (db.ranges ?? {})[jdId]
    const spread = spreadIsSane(db.companySpread) ? db.companySpread : DEFAULT_SPREAD
    const offered = offeredPcts(bench?.band)
    const pct = pctFor(decision?.percentile, offered)
    const market = figureAt(bench?.band, pct)
    const mine = decision ? { median: decision.median } : undefined
    const mid = accepted(mine, market)
    const width = spreadFor(spread, decision?.spread).spread
    const band = mid === null ? null : bandFor(mid, width)
    const from = db.market.surveys.find((sv) => sv.id === bench?.surveyId)
    const named = from ? `${from.provider} ${from.year}`.trim() : null

    let state: RangeState = 'banded'
    if (!bench || bench.failedReason) state = 'never-run'
    else if (bench.noMatchReason !== null || !bench.band) state = 'no-match'
    else if (bench.jdVersion !== jd.version) state = 'out-of-date'

    return {
      jdId: jd.id, title: jd.title, family: jd.family, level: jd.level,
      location: jd.location, state,
      minimum: band?.minimum ?? null,
      midpoint: band?.midpoint ?? null,
      maximum: band?.maximum ?? null,
      spread: width,
      overridden: decision?.spread !== undefined,
      companySpread: spread,
      percentile: pct,
      percentiles: offered,
      currency: bench?.currency ?? null,
      unit: bench?.unit ?? null,
      confidence: bench?.confidence ?? null,
      survey: named,
      // With one survey there is nothing to weigh against, so none is returned.
      cuts: (bench?.cuts ?? []).filter((cut) => cut.chosen !== false).map((cut) => ({
        code: cut.code, title: cut.title,
        provider: cut.provider ?? named, year: cut.year ?? null,
        location: cut.location ?? null,
        median: cut[pct], orgs: cut.orgs, incumbents: cut.incumbents,
      })),
      waiting: waitingOn(mine, market),
      acceptedBy: decision?.acceptedBy ?? null,
      acceptedAt: decision?.acceptedAt ?? null,
      outOfDate: Boolean(bench) && bench.jdVersion !== jd.version,
      noMatchReason: bench?.noMatchReason ?? null,
    }
  },

  async setCompanySpread(spread) {
    await wait(140)
    requireAdmin(session())
    if (!spreadIsSane(spread)) {
      throw new ApiError(400, `A spread is a percentage between 0 and ${MOST_SPREAD}.`)
    }
    db.companySpread = spread
    save()
    return { spread }
  },

  async setRoleSpread(jdId, spread) {
    await wait(140)
    requireAdmin(session())
    if (!spreadIsSane(spread)) {
      throw new ApiError(400, `A spread is a percentage between 0 and ${MOST_SPREAD}.`)
    }
    const decision = firstDecision(jdId)
    db.ranges![jdId] = { ...decision, spread }
    save()
    return { jdId, spread, overridden: true }
  },

  async clearRoleSpread(jdId) {
    await wait(140)
    requireAdmin(session())
    const decision = firstDecision(jdId)
    const { spread: _gone, ...rest } = decision
    db.ranges![jdId] = rest
    save()
    const spread = spreadIsSane(db.companySpread) ? db.companySpread : DEFAULT_SPREAD
    return { jdId, spread, overridden: false }
  },

  /*
   * The same two refusals the route keeps: only a percentile this role's own
   * survey published, and the figure at it accepted as it is chosen so that
   * nothing is left waiting straight afterwards.
   */
  async setPercentile(jdId, percentile) {
    await wait(140)
    const me = session()
    requireAdmin(me)
    if (!isPct(percentile)) {
      throw new ApiError(400, 'A range sits on the 25th, 50th, 75th or 90th percentile.')
    }
    const bench = (db.benchmarks ?? {})[jdId]
    const offered = offeredPcts(bench?.band)
    if (!offered.includes(percentile)) {
      throw new ApiError(
        409,
        `The survey behind this role did not publish a ${pctLabel(percentile)} percentile, `
        + 'so there is no figure to centre a range on.',
      )
    }
    const figure = figureAt(bench?.band, percentile)!
    const held = (db.ranges ?? {})[jdId]
    db.ranges = db.ranges ?? {}
    db.ranges[jdId] = {
      ...(held ?? { jdId, median: figure }),
      jdId, median: figure, percentile,
      acceptedBy: me.name, acceptedAt: new Date().toISOString(),
    }
    save()
    const spread = spreadIsSane(db.companySpread) ? db.companySpread : DEFAULT_SPREAD
    const width = spreadFor(spread, db.ranges[jdId].spread).spread
    return { jdId, percentile, ...bandFor(figure, width), spread: width }
  },

  async acceptRange(jdId) {
    await wait(220)
    const me = session()
    requireAdmin(me)
    const bench = (db.benchmarks ?? {})[jdId]
    if (!bench) throw new ApiError(409, 'That role has not been benchmarked yet.')
    const decision = (db.ranges ?? {})[jdId]
    const pct = pctFor(decision?.percentile, offeredPcts(bench.band))
    const market = figureAt(bench.band, pct)
    const waits = waitingOn(decision ? { median: decision.median } : undefined, market)
    if (!waits) throw new ApiError(409, 'There is nothing waiting on this role.')

    db.ranges = db.ranges ?? {}
    db.ranges[jdId] = {
      ...(decision ?? { jdId, median: waits.to }),
      jdId, median: waits.to, percentile: pct,
      acceptedBy: me.name, acceptedAt: new Date().toISOString(),
    }
    save()
    const spread = spreadIsSane(db.companySpread) ? db.companySpread : DEFAULT_SPREAD
    const width = spreadFor(spread, db.ranges[jdId].spread).spread
    return { jdId, ...bandFor(waits.to, width), spread: width }
  },

  async acceptAllRanges() {
    await wait(280)
    const me = session()
    requireAdmin(me)
    const marks = db.benchmarks ?? {}
    db.ranges = db.ranges ?? {}
    const at = new Date().toISOString()
    let moved = 0
    for (const jd of db.jds) {
      const decision = db.ranges[jd.id]
      if (!decision) continue
      const pct = pctFor(decision.percentile, offeredPcts(marks[jd.id]?.band))
      const market = figureAt(marks[jd.id]?.band, pct)
      const waits = waitingOn({ median: decision.median }, market)
      if (!waits) continue
      db.ranges[jd.id] = {
        ...decision, median: waits.to, percentile: pct, acceptedBy: me.name, acceptedAt: at,
      }
      moved += 1
    }
    save()
    return { accepted: moved }
  },

  /*
   * Publishing, and the same two refusals the route keeps: it publishes what
   * is there rather than what is waiting, and nothing afterwards moves it.
   */
  async publishRanges() {
    await wait(320)
    const me = session()
    requireAdmin(me)
    const marks = db.benchmarks ?? {}
    const held = db.ranges ?? {}
    const spread = spreadIsSane(db.companySpread) ? db.companySpread : DEFAULT_SPREAD

    const rows: PublishedRange[] = []
    for (const jd of db.jds) {
      const decision = held[jd.id]
      if (!decision) continue
      const bench = marks[jd.id]
      // The accepted median, never the market's current one.
      const width = spreadFor(spread, decision.spread).spread
      const band = bandFor(decision.median, width)
      const from = db.market.surveys.find((sv) => sv.id === bench?.surveyId)
      rows.push({
        jdId: jd.id, title: jd.title, family: jd.family, level: jd.level,
        location: jd.location, ...band, spread: width,
        percentile: pctFor(decision.percentile, offeredPcts(bench?.band)),
        currency: bench?.currency ?? null,
        unit: bench?.unit ?? null,
        survey: from ? `${from.provider} ${from.year}`.trim() : null,
      })
    }
    if (rows.length === 0) {
      throw new ApiError(409, 'There are no ranges to publish yet. Benchmark some roles first.')
    }

    const was = new Map((db.published?.items ?? []).map((r) => [r.jdId, r]))
    const changed = rows.filter((r) => {
      const old = was.get(r.jdId)
      return old && (old.midpoint !== r.midpoint || old.minimum !== r.minimum || old.maximum !== r.maximum)
    }).length
    const removed = [...was.keys()].filter((id) => !rows.some((r) => r.jdId === id)).length

    const set = {
      publishedAt: new Date().toISOString(),
      publishedBy: me.name,
      items: rows,
    }
    db.published = set
    save()
    return {
      publishedAt: set.publishedAt, publishedBy: set.publishedBy, count: rows.length,
      added: rows.filter((r) => !was.has(r.jdId)).length, changed, removed,
    }
  },

  async publishedRanges() {
    await wait()
    requireAdmin(session())
    // Nothing is downloadable until something is published.
    return db.published ?? { publishedAt: null, publishedBy: null, items: [] }
  },

  async saveBenchmarkCuts(jdId, codes) {
    await wait()
    requireAdmin(session())
    const bench = (db.benchmarks ?? {})[jdId]
    if (!bench) throw new ApiError(404, 'That job description has not been benchmarked yet.')
    const known = new Map(bench.cuts.map((c) => [c.code, c]))
    const chosen = codes.filter((c) => known.has(c))
    if (chosen.length === 0) {
      throw new ApiError(400, 'Choose at least one survey job for the band to be built from.')
    }
    const kept = chosen.map((c) => known.get(c)!)
    const total = kept.reduce((sum, c) => sum + Math.max(c.incumbents, 1), 0)
    const at = (pick: (c: BenchmarkCut) => number | null): number | null => {
      let sum = 0, weight = 0
      for (const cut of kept) {
        const v = pick(cut)
        if (v === null) continue
        const w = Math.max(cut.incumbents, 1)
        sum += v * w; weight += w
      }
      return weight === 0 ? null : Math.round((sum / weight) * 100) / 100
    }
    const p50 = at((c) => c.p50)
    // Absent stays absent, the way the real blend now does it. Filling a
    // percentile in from its neighbour is inventing a figure, and a mock that
    // invents one hides the screen that prints it.
    const band = p50 === null ? null : {
      p25: at((c) => c.p25), p50,
      p75: at((c) => c.p75), p90: at((c) => c.p90),
    }
    const jd = db.jds.find((j) => j.id === jdId)
    // Out of thousands, into the survey's units. See makeBenchmark.
    const mid = jd ? ((Number(jd.payMin) + Number(jd.payMax)) / 2) * 1000 : 0
    const ratio = band && band.p50 > 0 && mid > 0 ? Math.round((mid / band.p50) * 1000) / 1000 : null
    bench.cuts = kept.map((c) => ({ ...c, weight: Math.round((Math.max(c.incumbents, 1) / total) * 1000) / 1000 }))
    bench.band = band
    bench.compaRatio = ratio
    // Second copy of `position` in backend/src/lib/benchmark.ts, because the
    // frontend does not import backend code. Change both. The last time this
    // mock was allowed to reason its own way it derived the band from the
    // role's own pay range, agreed with itself, and hid a compa-ratio that
    // was a thousand times out.
    const hasPay = mid > 0
    bench.position = ratio === null ? (hasPay ? 'not benchmarked' : 'no pay range set')
      : ratio > 1.05 ? 'above market' : ratio < 0.95 ? 'below market' : 'at market'
    bench.runBy = session()?.name ?? 'You'
    bench.runAt = new Date().toISOString()
    save()
    return { ...bench }
  },

  async listSurveys() {
    await wait()
    requireAdmin(session())
    // Resolved on the way out, the way the route resolves it. A stand-in
    // that served surveys without it would draw a table of blank columns.
    return db.market.surveys.map((s) => withStatus({ ...s, shown: shownColumns(s.columns ?? []) }))
  },

  async getSurveyFilters(surveyId) {
    await wait()
    requireAdmin(session())
    return surveyFilters(db.market, surveyId)
  },

  async getColumnValues(surveyId, columnKey) {
    await wait()
    requireAdmin(session())
    return columnValues(db.market, surveyId, columnKey)
  },

  async removeSurvey(surveyId, confirmProvider) {
    await wait(420)
    requireAdmin(session())
    const records = dropSurvey(db.market, surveyId, confirmProvider)
    save()
    return { records }
  },

  async startSurveyUpload({ file, provider, year, industry, surveyId }) {
    await wait(420)
    const me = session()
    requireAdmin(me)

    // A survey file is a spreadsheet, always. Refused on the name here as the
    // API does, before anything is read.
    if (!/\.(csv|xlsx)$/i.test(file.name)) {
      const ext = file.name.includes('.') ? file.name.slice(file.name.lastIndexOf('.')) : 'no extension'
      const advice = /\.xls$/i.test(file.name)
        ? 'That is the older Excel format. Open it and use Save As to make a .xlsx or a .csv.'
        : 'A survey takes a .csv or a .xlsx, which is what a provider sends.'
      throw new ApiError(400, `"${file.name}" is ${ext}. ${advice}`)
    }

    const existing = surveyId ? db.market.surveys.find((s) => s.id === surveyId) : undefined
    if (surveyId && !existing) throw new ApiError(404, 'That survey no longer exists.')

    const p = existing ? existing.provider : (provider ?? '').trim()
    const y = existing ? existing.year : (year ?? '').trim()
    const ind = existing ? existing.industry : (industry ?? '').trim()
    if (!existing) {
      if (!p) throw new ApiError(400, 'Say which provider this survey came from.')
      if (!y) throw new ApiError(400, 'Say which year this survey covers.')
    }

    const text = /\.xlsx$/i.test(file.name)
      ? await xlsxToCsv(await file.arrayBuffer())
      : await file.text()
    const read = readSurveyFile(text)

    const jobId = `up_${Math.random().toString(36).slice(2, 10)}`
    const job: UploadJob & { startedAt?: number } = {
      id: jobId,
      kind: 'market',
      fileName: file.name,
      // **Not validated yet, and that matters.** On the deployed system the
      // upload finishing is not the file being read: the file landing is what
      // starts a Lambda, which calls the model and reads every row. A job
      // comes back with no columns on it.
      //
      // This stand-in reads the file in the same breath and could honestly
      // answer 'validated' here. It does not, because a stand-in that is
      // quicker than the product hides a wait exactly as well as a lenient one
      // hides a refusal, and the screen was once written against timing that
      // existed only here.
      status: 'extracting',
      startedAt: Date.now(),
      createdAt: new Date().toISOString(),
      market: {
        provider: p,
        year: y,
        industry: ind,
        ...(existing ? { surveyId: existing.id } : {}),
        columns: read.columns,
        samples: read.samples,
        filters: read.filters,
        unreadable: read.unreadable,
        thin: read.thin,
        rowsRead: read.rows.length,
        rowsWritten: 0,
        // A heading this survey has never had will be added to it, and the
        // rows already stored simply will not have it.
        ...(existing
          ? {
              newColumns: read.columns
                .filter(
                  (c) =>
                    !existing.columns.some(
                      (h) => h.heading.trim().toLowerCase() === c.heading.trim().toLowerCase(),
                    ),
                )
                .map((c) => c.heading),
            }
          : {}),
      },
      // Warns and never blocks. Two cuts from one provider in one year is a
      // normal thing to own.
      possibleDuplicates: duplicatesAmong(db.market.surveys, existing ? '' : p, existing ? '' : y, file.name),
    }
    db.uploads[jobId] = job as typeof db.uploads[string]
    marketRows.set(jobId, read.rows)
    save()
    return job
  },

  async confirmColumns(id, columns) {
    await wait(320)
    requireAdmin(session())
    const job = db.uploads[id]
    if (!job || job.kind !== 'market') {
      throw new ApiError(400, 'Only a survey upload has column names to confirm.')
    }
    if (job.status === 'imported' || job.status === 'storing') {
      throw new ApiError(400, 'The rows for this survey are already going in.')
    }
    const held = job.market?.columns ?? []
    if (columns.length !== held.length) {
      throw new ApiError(
        400,
        `This file has ${held.length} columns and ${columns.length} came back. ` +
        'A column can be renamed but not added or removed.',
      )
    }
    // The key and the heading are not hers to change. The key is what values
    // are already stored under, and the heading is the provider's.
    const next: SurveyColumn[] = held.map((col, i) => ({
      key: col.key,
      heading: col.heading,
      // The correction this screen exists for.
      name: (columns[i]?.name ?? col.name).trim() || col.name,
      kind: columns[i]?.kind ?? col.kind,
      ...(columns[i]?.currency && /^[A-Z]{3}$/.test(columns[i].currency!)
        ? { currency: columns[i].currency }
        : {}),
    }))
    job.market!.columns = next
    save()
    return { columns: next }
  },

  async commitSurvey(id, dropThin) {
    await wait(420)
    const me = session()
    requireAdmin(me)
    const job = db.uploads[id]
    if (!job || job.kind !== 'market') throw new ApiError(404, 'That upload no longer exists.')
    if (job.status === 'imported') {
      return { accepted: true, expected: job.market?.rowsWritten ?? 0 }
    }
    if (job.status !== 'validated') throw new ApiError(400, 'That file has not finished being read yet.')

    const rows = marketRows.get(id) ?? []
    const survey = storeSurvey(db.market, job, job.market!, rows, dropThin === true, me.name)
    const expected = rows.length - (dropThin ? job.market?.thin?.count ?? 0 : 0)

    job.status = 'imported'
    job.market!.rowsWritten = expected
    job.market!.surveyId = survey.id
    committedAt.set(survey.id, Date.now())
    save()
    return { accepted: true, expected }
  },

  // ---------------------------------------------------------------------------
  // Setting up companies. Every method refuses anybody but a super admin,
  // exactly as the API does.
  // ---------------------------------------------------------------------------

  async listCompanies() {
    await wait()
    requireSuperAdmin(session())
    return Admin.summarise(db.admin)
  },

  async getCompany(slug) {
    await wait()
    requireSuperAdmin(session())
    return Admin.detail(db.admin, slug)
  },

  async createCompany(input) {
    await wait(520)
    const me = session()
    requireSuperAdmin(me)
    const made = Admin.createCompany(db.admin, actor(me), input)
    save()
    return made
  },

  async addDomain(slug, domain) {
    await wait(300)
    const me = session()
    requireSuperAdmin(me)
    const c = Admin.must(db.admin, slug)
    const tidy = Admin.tidyDomain(domain)
    const bad = Admin.checkDomain(tidy)
    if (bad) throw new ApiError(400, bad)
    const owner = Admin.domainOwner(db.admin, tidy)
    if (owner && owner.slug !== slug) {
      throw new ApiError(409, `${tidy} already signs in to another company.`)
    }
    if (!owner) {
      c.domains.push(tidy)
      Admin.note(c, actor(me), 'domain.added', { detail: { domain: tidy } })
      save()
    }
    return [...c.domains].sort()
  },

  async removeDomain(slug, domain) {
    await wait(300)
    const me = session()
    requireSuperAdmin(me)
    const c = Admin.must(db.admin, slug)
    if (!c.domains.includes(domain)) throw new ApiError(404, 'Not found.')
    // The last one cannot go, or nobody here can sign in with their email.
    if (c.domains.length <= 1) {
      throw new ApiError(
        400,
        'This is the only domain, and removing it would stop everyone here signing in with their email. Add another one first.',
      )
    }
    c.domains = c.domains.filter((d) => d !== domain)
    Admin.note(c, actor(me), 'domain.removed', { detail: { domain } })
    save()
    return [...c.domains].sort()
  },

  async setCompanySso(slug, connection) {
    await wait(300)
    const me = session()
    requireSuperAdmin(me)
    const c = Admin.must(db.admin, slug)
    const wanted = (connection ?? '').trim() || null
    if (wanted && !/^[A-Za-z0-9](?:[A-Za-z0-9-]{0,126}[A-Za-z0-9])?$/.test(wanted)) {
      throw new ApiError(400, 'A connection name can only use letters, numbers and dashes, and cannot start or end with a dash.')
    }
    if (wanted && db.admin.companies.some((o) => o.slug !== slug && o.ssoConnection?.toLowerCase() === wanted.toLowerCase())) {
      throw new ApiError(409, `${wanted} already belongs to another company.`)
    }
    const was = c.ssoConnection ?? null
    if (wanted !== was) {
      c.ssoConnection = wanted ?? undefined
      Admin.note(c, actor(me), 'company.sso', { detail: { from: was ?? 'none', to: wanted ?? 'none' } })
      save()
    }
    return c.ssoConnection ?? null
  },

  async addPerson(slug, input) {
    await wait(480)
    const me = session()
    requireSuperAdmin(me)
    // A super admin putting their own address into a customer would give them
    // an admin's view of that customer's pay data under a second login.
    if (Admin.isSuperAdminEmail(input.email, me.email)) {
      throw new ApiError(403, 'That address cannot be added to a company.')
    }
    const made = Admin.addPerson(db.admin, actor(me), slug, input)
    save()
    return made
  },

  async setPersonRole(slug, userId, role) {
    await wait(300)
    const me = session()
    requireSuperAdmin(me)
    const person = Admin.mustPerson(db.admin, slug, userId)
    if (person.role !== role) {
      const was = person.role
      person.role = role
      Admin.note(Admin.must(db.admin, slug), actor(me), 'person.role', {
        personEmail: person.email,
        detail: { from: was, to: role, role },
      })
      save()
    }
    return Admin.toPerson(person)
  },

  async setPersonAccess(slug, userId, disabled) {
    await wait(300)
    const me = session()
    requireSuperAdmin(me)
    const person = Admin.mustPerson(db.admin, slug, userId)
    person.disabled = disabled
    Admin.note(Admin.must(db.admin, slug), actor(me), 'person.access', {
      personEmail: person.email,
      detail: { access: disabled ? 'turned off' : 'turned back on' },
    })
    save()
    return Admin.toPerson(person)
  },

  async resetPassword(slug, userId) {
    await wait(420)
    const me = session()
    requireSuperAdmin(me)
    const person = Admin.mustPerson(db.admin, slug, userId)
    if (Admin.must(db.admin, slug).ssoConnection) {
      throw new ApiError(400, 'This person logs in with their company single sign-on, so there is no password to send.')
    }
    const password = Admin.makePassword()
    person.password = password
    // Temporary again. The point of a reset is that the new one gets them to
    // the screen where they choose their own, and no further.
    person.temporary = true
    Admin.note(Admin.must(db.admin, slug), actor(me), 'person.password', {
      personEmail: person.email,
    })
    save()
    return password
  },

  async setPassword(slug, userId, password, confirm) {
    await wait(420)
    const me = session()
    requireSuperAdmin(me)
    const person = Admin.mustPerson(db.admin, slug, userId)
    if (Admin.must(db.admin, slug).ssoConnection) {
      throw new ApiError(400, 'This person logs in with their company single sign-on, so there is no password to set.')
    }
    if (!password) throw new ApiError(400, 'Type the new password.')
    if (confirm !== password) throw new ApiError(400, 'The two passwords do not match.')
    // Auth0's password policy, as the deployed one passes its reason on.
    if (password.length < 10) {
      throw new ApiError(400, 'Auth0 did not accept this password: Password is too weak. Try a longer one with a mix of letters, numbers and symbols. Nothing was changed.')
    }
    person.password = password
    person.temporary = false
    Admin.note(Admin.must(db.admin, slug), actor(me), 'person.password.set', { personEmail: person.email })
    save()
  },

  /* ---------- Match a job ---------- */

  async matchOverview() {
    await wait()
    requireAdmin(session())
    return { hasSurvey: db.market.surveys.length > 0 }
  },

  async startMatchChat() {
    await wait()
    requireAdmin(session())
    const chatId = `chat_${Date.now().toString(36)}`
    db.chats = db.chats ?? {}
    db.chats[chatId] = { chatId, startedAt: new Date().toISOString(), turns: [], running: null }
    save()
    return { chatId, turns: [] }
  },

  async matchChat(chatId) {
    await wait()
    requireAdmin(session())
    const chat = (db.chats ?? {})[chatId]
    if (!chat) throw new ApiError(404, 'That chat is not there. Start a new one.')
    // The same three states the real one counts as going.
    const going = Object.values(db.matches ?? {}).find(
      (m) => m.chatId === chatId && ['queued', 'reading', 'matching'].includes(m.state),
    )
    return { ...chat, running: going ? going.matchId : null }
  },

  async forgetMatchChat(chatId) {
    await wait()
    requireAdmin(session())
    delete (db.chats ?? {})[chatId]
    save()
  },

  async sendToMatch(chatId, text) {
    await wait()
    requireAdmin(session())
    const chat = (db.chats ?? {})[chatId]
    if (!chat) throw new ApiError(404, 'That chat is not there. Start a new one.')
    if (text.trim() === '') {
      throw new ApiError(400, 'There is nothing to match. Paste a job description, or type a job title.')
    }

    db.matches = db.matches ?? {}
    /*
     * The same three states the real one counts as going.
     *
     * Listing the finished ones instead let a stopped match read as still
     * running, so the next thing anybody sent was refused with "one is
     * already running" and nothing happened. A mock that disagrees with the
     * real rule is worse than no mock.
     */
    const GOING = ['queued', 'reading', 'matching']
    const going = Object.values(db.matches).find(
      (m) => m.chatId === chatId && GOING.includes(m.state),
    )
    if (going) {
      return {
        matchId: going.matchId,
        alreadyGoing: true,
        message: 'A match is already running in this chat. It will answer here when it is done.',
      }
    }

    /*
     * A follow-up, by the same rule the real one uses: a question asked once a
     * role has already been matched here. Anything that is not a question is a
     * role, including a second job description pasted into the same box.
     */
    const matchedAlready = chat.turns.some((t) => t.readAs !== null && t.answeredAt !== null)
    const asQuestion = text.trim().length <= 200 && (
      /^(why this match|show alternatives|total comp|one.?line range)\b/i.test(text.trim())
      || /\?\s*$/.test(text.trim())
      || /^(why|what|how|who|when|where|which|can|could|would|does|do|did|is|are|was|should|tell|show|explain|give|just|any|more)\b/i.test(text.trim())
    )
    const follow = matchedAlready && asQuestion

    // How the real one reads it: short and bare is a title, anything with the
    // headings a job description carries is a job description.
    const bare = text.trim().length <= 200
      && !/responsibilit|requirement|qualification|duties|key skills/i.test(text)
    const nonsense = /^[\d\W]+$/.test(text.trim()) || text.trim().split(/\s+/).length < 2

    const readAs: ReadAs = follow ? null : nonsense ? 'not-a-job' : bare ? 'title' : 'description'
    const matchId = `match_${Date.now().toString(36)}`
    db.matches[matchId] = {
      matchId, chatId, state: 'queued', stage: 'Waiting to start',
      startedAt: new Date().toISOString(), finishedAt: null,
      answerSoFar: '', failedReason: null, failedDetail: null,
      readAs, note: '', secondsSoFar: 0,
    }
    db.pendingAsks = db.pendingAsks ?? {}
    db.pendingAsks[matchId] = text
    save()
    return { matchId, chatId }
  },

  async saveMatchAsDraft(chatId) {
    await wait()
    const me = session()
    requireAdmin(me)
    const chat = (db.chats ?? {})[chatId]
    if (!chat) throw new ApiError(404, 'That chat is not there. Start a new one.')
    /*
     * The same rule the real one keeps: only a full job description.
     *
     * A title on its own would make a role with a title and nothing else,
     * sitting in the library looking finished.
     */
    const pasted = chat.turns.find((t) => t.readAs === 'description')
    if (!pasted) {
      throw new ApiError(
        409,
        'Only a full job description can be saved as a draft. A job title on its own would '
        + 'make a role with nothing in it.',
      )
    }
    const lines = pasted.asked.split('\n').map((l) => l.trim()).filter(Boolean)
    const jd: JobDescription = {
      id: id('jd-'),
      title: (lines[0] ?? 'Untitled role').slice(0, 120),
      family: FAMILIES[0],
      industry: industryFor(FAMILIES[0]),
      level: 'Mid',
      location: '',
      payMin: 0,
      payMax: 0,
      summary: lines.slice(1).join(' ').slice(0, 600),
      responsibilities: [],
      skills: [],
      quals: [],
      status: 'Draft',
      version: 1,
      updated: today(),
      createdBy: me.name,
      updatedBy: me.name,
    }
    db.jds = [jd, ...db.jds]
    db.versions[jd.id] = [
      { version: 1, date: today(), note: 'Saved from Match a job', by: me.name },
    ]
    save()
    return { id: jd.id, title: jd.title, status: jd.status }
  },

  async stopMatch(matchId) {
    await wait()
    requireAdmin(session())
    const match = (db.matches ?? {})[matchId]
    if (!match) throw new ApiError(404, 'That match is not there.')
    // A finished match is left alone. Pressing stop as the answer lands is a
    // race nobody should lose.
    if (match.state !== 'done' && match.state !== 'failed') {
      match.state = 'stopped'
      match.stage = 'Stopped'
      match.finishedAt = new Date().toISOString()
      save()
    }
    return { state: match.state }
  },

  async matchProgress(matchId) {
    await wait()
    requireAdmin(session())
    const match = (db.matches ?? {})[matchId]
    if (!match) throw new ApiError(404, 'That match is not there.')

    /*
     * One stage per poll, so the waiting states on the screen can actually be
     * watched rather than jumping from nothing to finished. The real one takes
     * 30 seconds to 3 minutes over the same three stages.
     */
    // A stopped match stays stopped and writes nothing down.
    if (match.state === 'stopped') return match

    if (match.state === 'queued') {
      match.state = 'reading'
      match.stage = 'Reading what you sent'
    } else if (match.state === 'reading') {
      match.state = 'matching'
      match.stage = match.readAs === null
        ? 'Looking back at the match'
        : 'Matching against your market data'
      match.answerSoFar = match.readAs === null ? '' : mockNote(match.readAs)
    } else if (match.state === 'matching') {
      const asked = (db.pendingAsks ?? {})[matchId] ?? ''
      match.note = match.readAs === null ? '' : mockNote(match.readAs)
      match.answerSoFar = match.readAs === null
        ? mockFollowUp(asked)
        : mockAnswer(match.readAs, match.note)
      match.state = 'done'
      match.stage = 'Done'
      match.finishedAt = new Date().toISOString()
      const chat = (db.chats ?? {})[match.chatId]
      if (chat) {
        chat.turns.push({
          turnId: `turn_${Date.now().toString(36)}`,
          asked,
          askedAt: match.startedAt,
          answer: match.answerSoFar,
          answeredAt: match.finishedAt,
          readAs: match.readAs,
          note: match.note,
        })
      }
    }
    match.secondsSoFar = Math.max(
      0, Math.round((Date.now() - Date.parse(match.startedAt)) / 1000),
    )
    save()
    return match
  },

  /*
   * Iconic (STE-997). The same routes, refusals and states as the server, with
   * an answer worked out from the mock's own data and moved on one step each
   * time the panel checks, so Thinking and the answer can both be watched.
   */
  async iconicChats(q = '') {
    await wait()
    const me = iconicPerson()
    const want = q.trim().toLowerCase()
    const chats = Object.values(iconicDb().chats)
      .filter((c) => c.owner === me.userId)
      .filter((c) => !want || c.title.toLowerCase().includes(want) || c.searchText.toLowerCase().includes(want))
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
    return { chats: chats.map(publicChat), more: null }
  },

  async startIconicChat() {
    await wait()
    const me = iconicPerson()
    const now = new Date().toISOString()
    const chat: MockIconicChat = {
      chatId: id('ic_'), owner: me.userId, title: 'New chat', titleSetByPerson: false,
      startedAt: now, updatedAt: now, turnCount: 0, searchText: '',
    }
    iconicDb().chats[chat.chatId] = chat
    iconicDb().turns[chat.chatId] = []
    save()
    return publicChat(chat)
  },

  async iconicChat(chatId) {
    await wait()
    const chat = ownChat(chatId)
    return { chat: publicChat(chat), turns: (iconicDb().turns[chatId] ?? []).map(publicTurn), more: false }
  },

  async renameIconicChat(chatId, title) {
    await wait()
    const chat = ownChat(chatId)
    const t = (title ?? '').trim()
    if (!t) throw new ApiError(400, 'Give the chat a name.')
    if (t.length > 80) throw new ApiError(400, 'Keep the name under 80 characters.')
    chat.title = t
    chat.titleSetByPerson = true
    save()
    return publicChat(chat)
  },

  async deleteIconicChat(chatId) {
    await wait()
    ownChat(chatId)
    delete iconicDb().chats[chatId]
    delete iconicDb().turns[chatId]
    save()
  },

  async askIconic(chatId, ask) {
    await wait()
    const me = iconicPerson()
    const chat = ownChat(chatId)
    const text = (ask.text ?? '').trim()
    if (!text) throw new ApiError(400, 'Ask Iconic something first.')
    if (text.length > 8000) throw new ApiError(400, 'Keep a question under 8,000 characters.')
    if (chat.running) throw new ApiError(409, 'Iconic is still answering. Wait or stop it.')
    const now = new Date().toISOString()
    const turns = iconicDb().turns[chatId] ?? []
    const qSeq = chat.turnCount + 1
    const aSeq = chat.turnCount + 2
    turns.push({ turnId: `${chatId}~${qSeq}`, seq: qSeq, kind: 'question', text, at: now, screen: ask.screen, record: ask.record })
    const answer: MockIconicTurn = {
      turnId: `${chatId}~${aSeq}`, seq: aSeq, kind: 'answer', text: '', at: now, state: 'queued',
      screen: ask.screen, record: ask.record, planned: mockIconicReply(me, text, ask.screen ?? '', ask.record),
    }
    turns.push(answer)
    iconicDb().turns[chatId] = turns
    if (!chat.titleSetByPerson && chat.turnCount === 0) chat.title = text.replace(/\s+/g, ' ').slice(0, 60)
    chat.turnCount = aSeq
    chat.updatedAt = now
    chat.searchText = `${chat.searchText}\n${text}`.slice(-8000)
    chat.lastScreen = ask.screen
    chat.running = answer.turnId
    save()
    return { turnId: answer.turnId }
  },

  async iconicTurn(turnId) {
    await wait()
    const { chat, turn } = ownTurn(turnId)
    if (turn.state === 'queued') {
      turn.state = 'thinking'
      turn.doing = `Reading ${turn.screen || 'Job Descriptions'}`
    } else if (turn.state === 'thinking' && turn.planned) {
      turn.state = 'done'
      turn.doing = ''
      turn.text = turn.planned.text
      turn.answeredAt = new Date().toISOString()
      if (turn.planned.action) {
        turn.action = { ...turn.planned.action, actionId: id('act_'), state: 'waiting', proposedAt: turn.answeredAt }
      }
      delete chat.running
    }
    save()
    return publicTurn(turn)
  },

  async stopIconicTurn(turnId) {
    await wait()
    const { chat, turn } = ownTurn(turnId)
    if (turn.state === 'queued' || turn.state === 'thinking') {
      turn.state = 'stopped'
      turn.doing = ''
      delete chat.running
      save()
    }
    return publicTurn(turn)
  },

  async confirmIconicAction(turnId, actionId) {
    await wait()
    const { chat, turn } = ownTurn(turnId)
    const a = turn.action
    if (!a || a.actionId !== actionId) throw new ApiError(404, 'Not found.')
    if (a.state !== 'waiting') return publicTurn(turn)
    const fresh = turn.seq === chat.turnCount && Date.now() - Date.parse(a.proposedAt) < 30 * 60 * 1000
    if (!fresh) {
      Object.assign(a, { state: 'failed', result: 'Not done. You never pressed OK. Ask again to do it today.', doneAt: new Date().toISOString() })
      save()
      throw new ApiError(409, 'Not done. You never pressed OK. Ask again to do it today.')
    }
    a.state = 'running'
    try {
      // The mock's own route, so the screen behind the panel changes as it would.
      if (a.kind === 'proposeSpread' && typeof turn.planned?.spread === 'number') await mockApi.setCompanySpread(turn.planned.spread)
      if (a.kind === 'proposePublishRanges') await mockApi.publishRanges()
      Object.assign(a, { state: 'done', result: 'Done.', link: turn.planned?.link ?? '', doneAt: new Date().toISOString() })
    } catch (err) {
      Object.assign(a, { state: 'failed', result: err instanceof Error ? err.message : 'That did not work.', doneAt: new Date().toISOString() })
    }
    save()
    return publicTurn(turn)
  },

  async cancelIconicAction(turnId, actionId) {
    await wait()
    const { turn } = ownTurn(turnId)
    const a = turn.action
    if (!a || a.actionId !== actionId) throw new ApiError(404, 'Not found.')
    if (a.state === 'waiting') {
      Object.assign(a, { state: 'cancelled', doneAt: new Date().toISOString() })
      save()
    }
    return publicTurn(turn)
  },
}

/* ---------- Iconic in mock mode ---------- */

interface MockIconicChat extends IconicChat { owner: string; searchText: string }
interface MockPlanned {
  text: string
  action?: Pick<IconicAction, 'kind' | 'summary' | 'warning'>
  spread?: number
  link?: string
}
interface MockIconicTurn extends IconicTurn { planned?: MockPlanned }

function iconicDb(): { chats: Record<string, MockIconicChat>; turns: Record<string, MockIconicTurn[]> } {
  db.iconic = db.iconic ?? { chats: {}, turns: {} }
  return db.iconic
}

/** As the server: HR admins and people managers, never a super admin. */
function iconicPerson(): Me {
  const me = session()
  if (me.role === 'superadmin') throw new ApiError(403, 'Not allowed.')
  return me
}

/** The person's own chat, or not found: someone else's is a chat that does not exist. */
function ownChat(chatId: string): MockIconicChat {
  const me = iconicPerson()
  const chat = iconicDb().chats[chatId]
  if (!chat || chat.owner !== me.userId) throw new ApiError(404, 'Not found.')
  return chat
}

function ownTurn(turnId: string): { chat: MockIconicChat; turn: MockIconicTurn } {
  const [chatId, seq] = turnId.split('~')
  const chat = ownChat(chatId)
  const turn = (iconicDb().turns[chatId] ?? []).find((t) => t.seq === Number(seq))
  if (!turn) throw new ApiError(404, 'Not found.')
  return { chat, turn }
}

const publicChat = ({ owner: _o, searchText: _s, ...c }: MockIconicChat): IconicChat => c
const publicTurn = ({ planned: _p, ...t }: MockIconicTurn): IconicTurn => JSON.parse(JSON.stringify(t))

/**
 * The mock's answer, from its own data. A few questions are understood; the
 * rest get a plain count of the library, never a figure the mock does not hold.
 */
function mockIconicReply(me: Me, text: string, screen: string, record?: string): MockPlanned {
  const t = text.toLowerCase()
  const role = record ? db.jds.find((j) => j.id === record) : undefined
  if (me.role === 'manager') {
    if (/market|survey|benchmark|salary|pay|range/.test(t)) {
      return { text: 'That is a question for your HR team. I can show you this role and its past versions, or help you ask HR for a change.' }
    }
    if (/ask hr|change/.test(t) && role) {
      return {
        text: 'Here is the request I will send. Nothing is sent until you press Send to HR.',
        action: { kind: 'proposeChangeRequest', summary: [`Ask HR to change “${role.title}”`, `Reason: ${text}`] },
        link: '/job-descriptions?tab=approvals',
      }
    }
  }
  if (me.role === 'admin') {
    const wider = /spread|wider|narrower/.exec(t)
    if (wider) {
      const n = Number((/(\d{1,2})\s*%/.exec(t) ?? [])[1] ?? 15)
      const now = spreadIsSane(db.companySpread) ? db.companySpread : DEFAULT_SPREAD
      return {
        text: `Here is what I will do. Nothing changes until you press Change spread.`,
        action: {
          kind: 'proposeSpread',
          summary: [`Change the range spread to ± ${n}%`, `Every role’s range goes from ± ${now}% to ± ${n}% around its market figure.`, 'Roles you set a spread for by hand keep their own.', 'Nothing is published. You still press Publish yourself.'],
        },
        spread: n, link: '/salary-ranges',
      }
    }
    if (/publish/.test(t) && screen === 'Salary Ranges') {
      return {
        text: 'Here is what I will do. Nothing is published until you press Publish.',
        action: { kind: 'proposePublishRanges', summary: ['Publish the salary ranges as they stand now', 'The published list is replaced.'] },
        link: '/salary-ranges',
      }
    }
    if (/no match/.test(t)) {
      const none = Object.values(db.benchmarks ?? {}).filter((b) => b.noMatchReason).length
      return { text: none === 0 ? 'No role came back with no match.' : `${plural(none, 'role')} found no match in your survey. Open Benchmarking to see them.` }
    }
  }
  const drafts = db.jds.filter((j) => j.status === 'Draft').length
  return { text: `You have ${plural(db.jds.length, 'job description')}, ${plural(drafts, 'draft')} among them. Ask me about one by name.` }
}

/** What the answer says about its own certainty, as the backend writes it. */
function mockNote(readAs: ReadAs): string {
  if (readAs === 'description') return 'Matched from the full job description you pasted.'
  if (readAs === 'title') {
    return 'Matched from a job title alone, so this answer is less certain than one from a '
      + 'full job description. A title does not say which area or industry the role sits in, '
      + 'what the role specifically focuses on, what the job actually does day to day. '
      + 'Pasting the full job description would give a sharper answer.'
  }
  return 'There is no job here to match.'
}

/**
 * A follow-up answer, from what the match already decided.
 *
 * Never a second match. The figures here are the ones the first answer gave,
 * because a follow-up that produced different numbers would be explaining a
 * match nobody ever saw.
 */
function mockFollowUp(asked: string): string {
  const q = asked.toLowerCase()
  if (q.includes('alternative')) {
    return [
      '## What else was looked at',
      '* **MWS.04.013.P3** — a rung too senior: it expects a team and this role has none.',
      '* **MWS.03.980.P2** — the right level, but quality assurance rather than regulatory affairs.',
    ].join('\n')
  }
  if (q.includes('total')) {
    return [
      'This survey reports base salary and total cash actual, and nothing beyond that.',
      '',
      'It does not report bonus or equity separately, so there is no figure here for either. '
      + 'To see those you would need a total remuneration survey from the same provider.',
    ].join('\n')
  }
  if (q.includes('range') || q.includes('one-line')) {
    return 'USD 118,400 to 161,250, median 139,781. Mercer 2025, annual base salary.'
  }
  return [
    '## Why this match',
    'The responsibilities name regulatory submissions and agency correspondence, which is what '
    + 'this survey job is defined by.',
    '',
    'The level came from two things: eight or more years stated, and no direct reports. That '
    + 'puts it at the second professional rung rather than the third.',
  ].join('\n')
}

function mockAnswer(readAs: ReadAs, note: string): string {
  if (readAs === 'not-a-job') {
    return 'There is no job description here to match.\n\n'
      + 'What you sent does not read as a role: it has no job title with responsibilities or '
      + 'requirements under it. Rather than matching it to whichever survey job happens to be '
      + 'closest, which would look like an answer and would not be one, nothing has been matched.'
  }
  return [
    note,
    '',
    '## Consultant II, Regulatory Affairs',
    'Survey job code **MWS.04.012.P2**.',
    '',
    '| What the market pays (annual) | USD |',
    '| -- | -- |',
    '| 25th percentile | 118,400 |',
    '| Median | 139,781 |',
    '| 75th percentile | 161,250 |',
    '| 90th percentile | not reported |',
    '',
    'Figures from Mercer 2025, in USD, reported annual. Nothing here comes from anywhere '
    + 'but this survey.',
    '',
    '## Why this one',
    'The responsibilities line up with regulatory submissions and agency correspondence, '
    + 'and the level matches a second rung professional with no direct reports.',
  ].join('\n')
}

/**
 * Rows read from a survey file, waiting for the columns to be confirmed.
 *
 * Kept out of the saved database on purpose. It is the stand-in for the
 * working file the real parse writes to storage, which is deleted once the
 * rows are safely stored, and putting a hundred thousand rows into
 * localStorage would fill it on the first survey.
 */
const marketRows = new Map<string, { line: number; values: Record<string, string> }[]>()

export const MOCK_CREDENTIALS = [
  { email: 'hr@stello.in', password: DEMO_PASSWORD, label: 'HR admin' },
  { email: 'manager@stello.in', password: DEMO_PASSWORD, label: 'People manager' },
]

export type { Role }

/** Shaped to exercise the preview: guessed fields, a note, and a real gap. */
const MOCK_DOC_ROLES: Omit<BulkRow, 'line' | 'errors'>[] = [
  {
    title: 'Associate Technical Consultant', family: 'Process Development', level: 'Junior',
    location: '', payMin: 0, payMax: 0,
    summary: 'The role supports drug development through technical tasks, process modeling and project management.',
    responsibilities: 'Process modeling, verification and troubleshooting|Upstream and downstream manufacturing development|Batch record review',
    skills: 'Strong communication, client management and teamwork|Proficiency in statistical software',
    quals: 'Bachelor’s degree in a science-related field|Experience with at least one clinical stage',
    status: 'Published', page: 2, guessed: ['family', 'level'],
    notes: 'The line “ADD SPECIFIC TECHNICAL SKILLS HERE” is an unfilled template placeholder and was not imported.',
    // Every section of its part of the document, as the real reader keeps them (FR-57).
    sections: [
      { heading: 'Job Description: Associate Technical Consultant', lines: [] },
      { heading: 'The Company:', lines: ['A development partner applying scientifically driven approaches to drug development.'] },
      { heading: 'Why Join Us?', lines: ['Mission-driven organization with substantial growth opportunities.', 'Competitive compensation, benefits, and inclusive culture.'] },
      { heading: 'Job Overview:', lines: ['The role supports drug development through technical tasks, process modeling and project management.'] },
      { heading: 'Primary Responsibilities:', lines: ['Process modeling, verification and troubleshooting', 'Upstream and downstream manufacturing development', 'Batch record review'] },
      { heading: 'Key Skills/Requirements:', lines: [
        'Bachelor’s degree in a science-related field',
        'Experience with at least one clinical stage',
        'Strong communication, client management and teamwork',
        'Proficiency in statistical software',
      ] },
    ],
  },
  {
    title: 'Technical Consultant', family: 'Process Development', level: 'Mid',
    location: '', payMin: 0, payMax: 0,
    summary: 'Serves as a subject matter expert in multiple areas and works autonomously with minimal supervision.',
    responsibilities: 'Write and review CMC sections for regulatory submissions|Serve as client representative at manufacturing facilities',
    skills: 'Ability to work independently and take ownership of projects|Proficient in project management',
    quals: 'Bachelor’s degree in a science-related field|Experience in at least two clinical stages',
    status: 'Published', page: 3, guessed: ['family', 'level'], notes: '',
    sections: [
      { heading: 'Job Description: Technical Consultant', lines: [] },
      { heading: 'Job Overview:', lines: ['Serves as a subject matter expert in multiple areas and works autonomously with minimal supervision.'] },
      { heading: 'Primary Responsibilities:', lines: ['Write and review CMC sections for regulatory submissions', 'Serve as client representative at manufacturing facilities'] },
      { heading: 'Key Skills/Requirements:', lines: ['Ability to work independently and take ownership of projects', 'Proficient in project management'] },
    ],
  },
  {
    title: 'Technical Vice President', family: 'Process Development', level: '',
    location: '', payMin: 0, payMax: 0,
    summary: 'Leads a technical team, owns large projects and is the primary point of contact for major clients.',
    responsibilities: 'Provide leadership and inspiration to team members|Manage staffing, hiring and resource allocation',
    skills: 'Strong communication, client management and teamwork',
    quals: 'Bachelor’s degree in a science-related field',
    status: 'Published', page: 5, guessed: ['family'],
    notes: 'Level is not stated anywhere in the document, so it was left empty rather than guessed.',
  },
]

/** A levelling grid: real titles, no prose, so every summary is empty. */
const MOCK_MATRIX_ROLES: Omit<BulkRow, 'line' | 'errors'>[] = [
  {
    title: 'Associate Consultant', family: '', level: '', location: '', payMin: 0, payMax: 0,
    summary: '',
    responsibilities: 'Process modeling, process scale-up and troubleshooting|Tech transfer from R&D to manufacturing|Batch record review, deviation and CAPA',
    skills: '', quals: '0 to 8 years of experience across three sub-levels',
    status: 'Published', page: 1, guessed: [],
    notes: 'Assembled from the matrix rows for this column. The document holds no written description of this role.',
  },
  {
    title: 'Senior Consultant', family: '', level: 'Senior', location: '', payMin: 0, payMax: 0,
    summary: '',
    responsibilities: 'CDMO and CMO selection and management|Analytical method control strategy and lifecycle management',
    skills: '', quals: '15+ years of experience',
    status: 'Published', page: 1, guessed: ['level'],
    notes: 'Assembled from the matrix rows for this column. The document holds no written description of this role.',
  },
]
